(ns ductile.ui.transport
  (:require ["framer-motion" :refer [AnimatePresence motion]]
            ["html5sortable/dist/html5sortable.cjs" :as Sortable]
            ["signature_pad" :as signature-pad]
            [applied-science.js-interop :as j]
            [clojure.edn :as edn]
            [clojure.set :as set]
            [clojure.string :as str]
            [ductile.clerk.render :as ductile.render]
            [ductile.compound-config :as compound-config]
            [ductile.scope-auth :as scope-auth]
            [ductile.time.format :as time-format]
            [ductile.ui.components :as ui]
            [ductile.ui.components.columns :as columns]
            [ductile.ui.components.combobox :as combobox]
            [ductile.ui.components.omnibox :as omnibox]
            [ductile.ui.components.table :as table]
            [ductile.ui.deframe :as deframe]
            [ductile.ui.fahrzeug :as fahrzeug]
            [ductile.ui.fields :as fields]
            [ductile.ui.http :as http]
            [ductile.ui.icons :as icons]
            [ductile.ui.load-builder.overview-map :as overview-map]
            [ductile.ui.locale :as locale :refer [tr trn]]
            [ductile.ui.logo :as ui.logo]
            [ductile.ui.mobile :as mobile]
            [ductile.ui.router :as router]
            [ductile.ui.routes :as routes]
            [ductile.ui.scope]
            [ductile.ui.util :as util]
            [ductile.workflow.parking-utils :as parking-utils]
            [goog.string :as gstring]
            [nextjournal.clerk.render :as render]
            [nextjournal.clerk.render.hooks :as hooks]
            [nextjournal.log :as log]
            [re-frame.core :as rf])
  (:require-macros [ductile.ui.scope.macros :refer [when-current-user-scope when-current-user-write-scope]]))

(def !route (deframe/!route-get ::route))
(def !waypoints (deframe/!route-get ::waypoints))
(def !origin (deframe/!route-get ::origin))

(def tx!
  overview-map/tx!)

(defn tw-class
  ([id] (tw-class id []))
  ([id extra-classes]
   (concat (get {:th ["text-slate-600" "text-[10px]" "px-2" "py-1" "bg-slate-100" "first:rounded-md-tl" "last:rounded-md-tr" "text-left" "whitespace-nowrap"]
                 :td ["px-2" "py-1" "text-[12px]" "whitespace-nowrap"]} id [])
           extra-classes)))

(defn location-name [transport waypoint]
  (if (= :mode/ship (:transport/mode transport))
    (:location/name waypoint (:compound/name waypoint))
    (:address/city waypoint)))

(defn self-pickup? [transport]
  (if (contains? transport :self-pickup?)
    (:self-pickup? transport)
    (contains? #{:self-pickup} (-> transport :transport/fahrzeuge first :dispo/flag))))

(defn italian-compound? [compound-name]
  (contains? #{"Civitavecchia" "Lonato" "Frosinone" "ARVAL-P3" "Piadena" "Ravenna"} compound-name))

(defn entry-transport? [{:as params :keys [transport]}]
  (= (:compound/name params)
     (get-in transport [:destination/location :compound/name])))

(defn make-bill-of-lading [{:as args :keys [transport]} waypoint]
  (let [document (first (filter #(= (:ductile/id waypoint) (-> % :destination/location :ductile/id))
                                (:transport/bills-of-lading transport)))
        entry-transport? (entry-transport? args)]
    (assoc waypoint
           :waypoint/bill-of-lading
           (when-not entry-transport?
             {:document document
              :loading? (:loading? document)
              :generate! (when (scope-auth/authorized-for? (:user/write-scopes @deframe/!current-user) :scope/bill-of-lading)
                           (fn [event]
                             (when event (.preventDefault event) (.stopPropagation event))
                             (deframe/tx! [[:op/assoc-in [:current-route/transient ::transport]
                                            (update transport
                                                    :transport/bills-of-lading
                                                    (fn [bols]
                                                      (map (fn [bol]
                                                             (if (= (:ductile/id waypoint)
                                                                    (-> bol :destination/location :ductile/id))
                                                               (assoc bol :loading? true)
                                                               bol))
                                                           bols)))]])
                             (.then
                              (http/$lambda
                               'ductile.transport/handle-create-bill-of-lading
                               {:compound-name (:compound/name args)
                                :customer-name (:customer/name waypoint)
                                :destination-id (:ductile/id waypoint)
                                :transport-id (:ductile/id transport)})
                              (fn [result]
                                (if (:error result)
                                  (do
                                    (deframe/route-assoc! ::error result)
                                    (deframe/tx! [[:op/update-in [:current-route/transient ::transport :transport/bills-of-lading]
                                                   (partial map (fn [bol]
                                                                  (if (= (:ductile/id waypoint)
                                                                         (-> bol :destination/location :ductile/id))
                                                                    (dissoc bol :loading?)
                                                                    bol)))]]))
                                  (let [doc (:document result)]
                                    (deframe/tx! [[:op/merge-at [:current-route/transient ::transport]
                                                   {:transport/bills-of-lading
                                                    (conj (vec (remove (comp #{(:ductile/id waypoint)} :ductile/id :destination/location)
                                                                       (:transport/bills-of-lading transport)))
                                                          (assoc doc :destination/location waypoint))}]
                                                  [:op/assoc :toast {:title (str "✅ " (tr "%1 generated" (:document/number doc)))
                                                                     :description (tr "The bill of lading for %1 has been successfully generated."
                                                                                      (location-name transport waypoint))
                                                                     :action {:as :a
                                                                              :href (router/path '[compound :compound-name bill-of-lading :ductile-id]
                                                                                                 {:ductile-id (:ductile/id doc)
                                                                                                  :compound-name (:compound/name waypoint)})
                                                                              :data-testid "show-bol"
                                                                              :variant :success
                                                                              :label (tr "Show %1" (:document/number doc))}
                                                                     :variant :success}]
                                                  [:op/dissoc-in [:current-route/transient ::error]]])))))))}))))

(defn mapbox-route [origin-coord waypoint-coords optimize?]
  (let [coords (map #(str/join "," %) (concat [origin-coord] waypoint-coords))
        url (str "https://api.mapbox.com/"
                 (if optimize?
                   "optimized-trips/v1/"
                   "directions/v5/")
                 "mapbox/driving/"
                 (str/join ";" coords)
                 "?access_token=" (.-accessToken js/mapboxgl)
                 "&geometries=geojson&overview=full"
                 "&approaches=" (.repeat ";curb" (dec (count coords)))
                 (if optimize?
                   "&source=first&destination=last&roundtrip=false"
                   "&waypoints_per_route=true"))]
    (.. (js/fetch url) (then #(.json %)))))

(defn sort-waypoints-by-transport-data [transport waypoints]
  (if-let [order (-> transport :ductile/data :destination/ductile-ids)]
    (vec (sort-by #(let [i (.indexOf order (:ductile/id %))]
                     (if (< i 0) (count waypoints) i))
                  waypoints))
    waypoints))

(defn make-waypoints [{:as args :keys [transport]}]
  (->> (:transport/fahrzeuge transport)
       (group-by :fahrzeug/destination)
       (mapv (fn [[waypoint fahrzeuge]]
               (assoc waypoint
                      :waypoint/fahrzeuge fahrzeuge
                      :waypoint/transport transport
                      :customer/name (-> fahrzeuge first :customer/name)
                      :compound/name (:compound/name args))))
       (mapv (partial make-bill-of-lading args))
       (sort-waypoints-by-transport-data transport)))

(defn make-origin [transport]
  (assoc (:origin/location transport)
         :waypoint/fahrzeuge (:transport/fahrzeuge transport)
         :waypoint/transport transport))

(defn route-preview
  ([origin waypoints]
   (route-preview {:transport/mode :mode/unknown} origin waypoints))
  ([transport origin waypoints]
   (str/join " → " (concat [(location-name transport origin)]
                           (map (partial location-name transport) waypoints)))))

(defn waypoints-viewer [{:as transport :keys [origin waypoints]}]
  [route-preview transport origin waypoints])

(defn waypoint-coord [{:location/keys [longitude latitude]}]
  [longitude latitude])

(defn transport-link
  ([transport] (transport-link nil transport))
  ([variant transport]
   (routes/path (cond-> '[compound :compound-name transports :ductile/id] variant (conj variant))
                {:ductile/id (:ductile/id transport)
                 :compound-name (or (-> transport :origin/location :compound/name)
                                    (:compound/name (deframe/current-route-params)))})))

(defn vehicle-count-badge [transport]
  (let [done (get-in transport [:transport/progress :done])
        total (get-in transport [:transport/progress :total])
        complete? (= done total)]
    [:span.inline-flex.gap-1.items-center.tabular-nums
     {:class (when complete? ["text-green8" "font-bold"])}
     [:div.w-3.h-3
      {:class (if complete? ["text-green8"] ["text-mauve10"])} icons/car]
     (tr "%1 of %2"
         ;; how many vehicles are parked at the transport's loading line
         done total)]))

(defn progress-bar [{:transport/keys [manifest-unknown?] :as transport}]
  (let [done (get-in transport [:transport/progress :done])
        total (get-in transport [:transport/progress :total])]
    (when-let [percentage (and (not manifest-unknown?)
                               (str (int (* 100 (/ done total)))
                                    "%"))]
      [:div.w-full.bg-mauve5.rounded-full.relative.h-3.shadow-inner
       [:div.bg-indigo-700.text-white.leading-none.rounded-full.h-3
        {:class "py-[2px] text-[11px]" :style {:width percentage}}]
       [:div.absolute.left-0.top-0.h-3.leading-none.flex.items-center.text-white
        {:class ["text-[10px]"]
         :style {:left (str "max(5px, calc(" percentage " - 35px))") :width 35}}
        percentage]])))

(defn exited? [{:keys [transport/status]}]
  (= :transport.status/exited status))

(defn misplaced-fahrzeuge-in-lines [{:keys [transports all-fahrzeuge]}]
  (let [misplaced (keep (fn [[loading-line fahrzeuge]]
                          (let [transport (first (filter #(= loading-line (:transport/loading-line %)) transports))
                                vins-in-transport (set (map :vehicle/vin (:transport/fahrzeuge transport)))
                                extra-fahrzeuge (remove (fn [f] (contains? vins-in-transport (:vehicle/vin f)))
                                                        fahrzeuge)]
                            (when (seq extra-fahrzeuge)
                              [loading-line extra-fahrzeuge])))
                        all-fahrzeuge)]
    (when (seq misplaced)
      [:div.mb-4.bg-red-50.border-red-400.border.p-3.rounded-md.shadow {:class "text-[12px]"}
       (into [:ul.list-disc.pl-5.space-y-2]
             (map (fn [[loading-line extra-fahrzeuge]]
                    [:li.text-red-500
                     [:span.font-medium (count extra-fahrzeuge) " vehicles on loading line "
                      [:span.font-bold (str (:zone loading-line) (:row loading-line))] " that should not be there: "]
                     [:span.block.mt-1 {:class "text-[11px]"}
                      (str/join ", " (map :vehicle/vin extra-fahrzeuge))]])
                  misplaced))])))

(defn dispo-badge [& content]
  (into [:div
         {:class "text-[10px] px-[5px] py-[3px] inline-flex rounded-full leading-none border border-slate8 bg-white"}]
        content))

(defn entry-transports-table-row [args transport]
  (let [link-opts (into {:compound-name (:compound/name args)} transport)
        entry-transport? (entry-transport? (assoc args :transport transport))]
    [:tr.even:bg-slate-50.hover:bg-indigo-50.border-b.group
     {:class (when (exited? transport) "opacity-50")}
     #_[:td {:class (tw-class :td ["text-right"])}
        [:a {:href (transport-link link-opts)}
         (ui/transport-status-badge (:transport/status transport))]]
     [:td {:class (tw-class :td ["border-l"])}
      [:a.flex.gap-2 {:href (transport-link link-opts)}
       [:span.gap-2.inline-flex
        (let [transport-label (or (:transport/name transport)
                                  (-> transport :transport/transporter :truck/number)
                                  (-> transport :transport/carrier ((some-fn :company/name :address/name)))
                                  (tr "Unassigned"))]
          [:span.hover:underline.hover:text-indigo-600
           transport-label
           (when (:transport/customer-names transport)
             [:span.text-gray-400
              {:class [(when transport-label "ml-1")]}
              (str/join ", " (remove nil? (:transport/customer-names transport)))])
           (when (and (self-pickup? transport) (not entry-transport?))
             [:div.flex.items-center
              (dispo-badge (tr "self pickup"))])])]]]
     [:td {:class (tw-class :td ["border-l"])}
      (when (:entry/datetime transport)
        (time-format/->str {:format :datetime} (:entry/datetime transport)))]
     [:td {:class (tw-class :td ["border-l"])}
      (when-let [{:keys [zone row place]} (:transport/loading-line transport)]
        (str/join "/" (filter some? [zone row place])))]
     [:td {:class (tw-class :td ["border-l"])}
      [:a.flex.hover:underline.hover:text-indigo-600 {:href (transport-link link-opts)}
       (progress-bar transport)]]
     [:td {:class (tw-class :td ["border-l" "text-right"])}
      [:a.flex.hover:underline.hover:text-indigo-600.justify-end {:href (transport-link link-opts)}
       (vehicle-count-badge transport)]]
     [:td {:class (tw-class :td ["border-l"])}
      [:a.text-indigo-600.hover:underline.mr-3
       {:class "text-[11px]"
        :href (transport-link link-opts)}
       "Details"]]]))

(defn rpad-seq [s n x]
  (take n (concat s (repeat x))))

(defn italian-bill-of-lading-view [{:keys [bill-of-lading]}]
  (let [{:keys [destination origin vehicles customer transporter customs-clearances transport]} (:document/data bill-of-lading)
        origin-address-block [:div.mt-1 {:class "text-[12px]"}
                              (-> origin :compound/company :company/name)
                              [:br]
                              (:address/street origin) [:br]
                              (:address/postcode origin) " "
                              (:address/city origin) " " "(" (:address/province-code origin) ")"]
        transporter-address-block [:div.mt-1 {:class "text-[12px]"}
                                   (:address/name transporter) [:br]
                                   (:address/street transporter) [:br]
                                   (:address/postcode transporter) " " (:address/city transporter) " " (when (:address/province-code transporter)
                                                                                                         (str "(" (:address/province-code transporter) ")"))]]
    [:div.border.bg-gray-200.w-full.print:w-auto.p-10.not-prose.print:p-0.print:bg-white.print:border-0.print:border-transparent.print-margin.a4
     [:div.min-h-screen.print:min-h-none
      [:div.mx-auto
       [:div.bg-white.max-w-4xl.mx-auto.p-12.shadow-lg.relative.print:max-w-none.print:shadow-none.print:p-0
        [:div.border.border-black
         [:div.flex
          [:div.border-black.border-r {:class "w-[70mm]"}
           [:div.border-b.border-black {:class "px-[5mm] py-[2mm]"}
            [:div.font-bold {:class "text-[10px]"} "Depositante"]
            [:div.mt-1 {:class "text-[12px]"}
             (:customer/name customer)
             [:br]
             (:address/street customer) [:br]
             (:address/postcode customer) " "
             (:address/city customer) " "
             (when (:address/province-code customer)
               (str "(" (:address/province-code customer) ")"))]]
           [:div.border-b.border-black {:class "px-[5mm] py-[2mm]"}
            [:div.font-bold {:class "text-[10px]"} "Mittente"]
            [:div.mt-1 {:class "text-[12px]"}
             origin-address-block]]
           [:div.border-b.border-black {:class "px-[5mm] py-[2mm]"}
            [:div.font-bold {:class "text-[10px]"} "Destinatario"]
            [:div.mt-1 {:class "text-[12px]"}
             (:address/name destination) [:br]
             (:address/street destination) [:br]
             (:address/postcode destination) " " (:address/city destination) " "
             "(" (:address/province-code destination) ")"]]
           [:div.border-b.border-black {:class "px-[5mm] py-[2mm]"}
            [:div.font-bold {:class "text-[10px]"} "Luogo di destinazione delle merci"]
            [:div.mt-1 {:class "text-[12px]"}
             (:address/name destination) [:br]
             (:address/street destination) [:br]
             (:address/postcode destination) " " (:address/city destination) " "
             "(" (:address/province-code destination) ")"]]
           [:div {:class "px-[5mm] py-[2mm]"}
            [:div.font-bold {:class "text-[10px]"} "Luogo e data di presa in carico delle merci"]
            [:div.mt-1 {:class "text-[12px]"}
             (:address/city origin) " (" (:address/province-code origin) ")" [:br]
             (time-format/->str {:format :date :locale :it} (:ductile/created-at bill-of-lading))]]]
          [:div.flex-auto
           [:div.border-b.border-black.flex.justify-between.items-center {:class "px-[5mm] py-[2mm]"}
            [:div
             [:div.text-lg.font-bold "Documento di Trasporto"]
             [:div
              {:class "text-[10px]"}
              "D.P.R. n. 472 del 14.08.1996 - D.P.R. n. 696 del 21.12.1996"]]
            [:div {:style {:width "18mm"}} [:img {:src (ui.logo/logo (-> bill-of-lading :ductile/location :compound/name))}]]]
           [:div.border-b.border-black {:class "px-[5mm] py-[2mm]"}
            [:div.flex.items-center
             [:div.font-bold.mr-6 {:class "text-[10px]"} "Trasporto a cura del:"]
             [:div.flex.items-center.gap-3
              [:div.flex.items-center.gap-1
               [:div.w-3.h-3.border.border-black]
               [:div {:class "text-[12px]"} "mittente"]]
              [:div.flex.items-center.gap-1
               [:div.w-3.h-3.border.border-black]
               [:div {:class "text-[12px]"} "destinatario"]]
              [:div.flex.items-center.gap-1
               [:div.w-3.h-3.border.border-black]
               [:div {:class "text-[12px]"} "vettore"]]]]
            [:div.flex.items-center.gap-6.mt-1
             [:div.flex.items-center.gap-3
              [:div.font-bold {:class "text-[10px]"} "N.DDT:"]
              [:div {:class "text-[10px]"} (:document/number bill-of-lading)]]
             [:div.flex.items-center.gap-3
              [:div.font-bold {:class "text-[10px]"} "Data:"]
              [:div {:class "text-[10px]"} (time-format/->str {:locale :it} (:ductile/created-at bill-of-lading))]]]
            #_[:div.flex.items-center.gap-3.mt-1
               [:div.font-bold {:class "text-[10px]"} "Cura:"]
               [:div {:class "text-[10px]"} "Trasferimento da conto deposito"]]]
           [:div.border-b.border-black {:class "px-[5mm] py-[2mm] h-[20mm]"}
            [:div.font-bold {:class "text-[10px]"} "Persona delegata al ritiro dei veicoli (in caso di ritiro per conto del destinatario)"]]
           [:div.border-b.border-black
            [:div.flex
             [:div.border-r.border-black.flex.items-center.gap-3
              {:class "w-1/2 px-[5mm] py-[2mm]"}
              [:div.font-bold {:class "text-[10px]"} "Camion n."]
              [:div {:class "text-[12px]"} (or (:transport/name transport)
                                               (-> transport :transport/transporter :truck/number))]]
             [:div.flex.items-center.gap-3
              {:class "w-1/2 px-[5mm] py-[2mm]"}
              [:div.font-bold {:class "text-[10px]"} "Lotto n."]
              [:div {:class "text-[12px]"} (or (:transport/load-number transport)
                                               (:load/id transport))]]]]
           [:div.border-b.border-black {:class "px-[5mm] py-[2mm]"}
            (when transporter
              [:<>
               [:div.font-bold {:class "text-[10px]"} "Trasportatore"]
               [:div.flex.gap-6
                transporter-address-block
                [:div {:class "text-[12px]"}
                 "N.ISCR.ALBO TRASPORTATORI" [:br]
                 "BS1311023"]]])]
           [:div.border-b.border-black
            [:div.flex
             [:div.border-r.border-black
              {:class "w-1/2 px-[5mm] py-[2mm]"}
              [:div.font-bold {:class "text-[10px]"} "Targa camion"]
              [:div {:class "text-[12px]"} (or (:transport/license-plate transport)
                                               (-> transport :transport/transporter :transport/license-plate))]]
             [:div
              {:class "w-1/2 px-[5mm] py-[2mm]"}
              [:div.font-bold {:class "text-[10px]"} "Autista"]
              [:div {:class "text-[12px]"} (or (:transport/driver-name transport)
                                               (-> transport :transport/transporter :transport/driver :transport/driver-name))]]]]
           [:div {:class "px-[5mm] py-[2mm]"}
            [:div.font-bold {:class "text-[10px]"} "Annotazioni - Variazioni"]]]]
         [:div.border-t.border-black {:class "px-[5mm] py-[2mm] h-[20mm]"}
          [:div.font-bold {:class "text-[12px]"} "Istruzioni particolari"]
          [:div
           (str (-> (:transport/loading-line transport) :parking-spot/zone :parking-zone/name)
                (-> (:transport/loading-line transport) :parking-spot/row))]
          [:div {:class "text-[12px]"} "In linea con la disposizione speciale 666 della normativa ADR"]]
         [:div
          [:table.w-full {:cellpadding 0 :cellspacing 0}
           [:thead.text-left
            [:tr
             [:th.font-bold.pb-1 {:class "text-[10px] pl-[2mm]"} "VIN (Ord.Nr./Targa/Kom.Nr.)"]
             [:th.font-bold.pb-1 {:class "text-[10px] pl-[2mm]"} "Marchio"]
             [:th.font-bold.pb-1 {:class "text-[10px] pl-[2mm]"} "Modelli"]
             [:th.font-bold.pb-1 {:class "text-[10px] px-[2mm]"} "Osservazioni (accessori)"]]]
           (into [:tbody]
                 (map (fn [vehicle]
                        [:tr.border-b.border-black.first:border-t.last:border-b-0
                         [:td.py-1 {:class "text-[10px] pl-[2mm] h-[30px]"} (:vehicle/vin vehicle)]
                         [:td.py-1 {:class "text-[10px] pl-[2mm]"} (get-in vehicle [:vehicle/make :make/name])]
                         [:td.py-1 {:class "text-[10px] pl-[2mm]"} (get-in vehicle [:vehicle/model-info :vehicle/model])]
                         [:td.py-1 {:class "text-[10px] px-[2mm]"}
                          [:p (when-let [customs-clearance (get customs-clearances (:vehicle/vin vehicle))]
                                (str (str/capitalize (name (:document/kind customs-clearance)))
                                     ": "
                                     (:document/number customs-clearance)))]
                          [:p (str/join ", " (:vehicle/equipment vehicle))]]]))
                 (rpad-seq vehicles (max (count vehicles) 10) []))]]
         [:div.border-t.border-black.flex
          [:div.border-r.border-black.flex.flex-col.justify-between {:class "w-1/3 px-[5mm] py-[2mm]"}
           origin-address-block
           [:div.font-bold.mt-1 {:class "text-[10px]"} "Firma e Timbro del Mittente"]]
          [:div.border-r.border-black.flex.flex-col.justify-between {:class "w-1/3 px-[5mm] py-[2mm]"}
           transporter-address-block
           [:div.font-bold.mt-1 {:class "text-[10px]"} "Firma e Timbro del Trasportatore"]]
          [:div.flex.flex-col.justify-between {:class "w-1/3 px-[5mm] py-[2mm]"}
           [:div.font-bold {:class "text-[10px]"} "Ricevuta della Merce"]
           [:div.mt-1.flex.gap-3
            [:div {:class "w-1/2"}
             [:div.border-b.border-dotted.border-black {:class "h-[5mm]"}]
             [:div {:class "text-[10px]"} "Luogo"]]
            [:div {:class "w-1/2"}
             [:div.border-b.border-dotted.border-black {:class "h-[5mm]"}]
             [:div {:class "text-[10px]"} "data e ora"]]]
           [:div.font-bold.mt-2 {:class "text-[10px]"} "Firma e Timbro del Destinatario"]]]]]]]]))

(defn list-item-input [{:keys [input label description error]}]
  [:li
   [:div.pl-4.flex.items-center.duration-300.active:duration-0.cursor-pointer.select-none.active:bg-black.active:bg-opacity-10
    [:div.pr-4.w-full.relative.pt-3.pb-4
     {:class (str "min-h-[44px] " mobile/hairline-b)}
     [:div
      {:class "text-[12px]"}
      (when label
        [:div.font-bold.mb-1 {:class "text-[12px]"} label])
      [:input (merge {:type "text" :class "block text-[12px] appearance-none w-full focus:outline-none bg-transparent placeholder-black placeholder-opacity-30 disabled:opacity-50 border border-slate-300 rounded shadow-inner bg-white px-2 py-1"} input)]
      (when error
        [:div.text-red-600
         {:class "text-[0.625rem]"}
         error])
      (when description
        [:div.text-black.text-opacity-50.line-clamp-2
         {:class "text-[0.625rem]"}
         description])]]]])

(defn list-item-radio [{:keys [input label]}]
  [:li
   [:label.pl-4.flex.items-center.duration-300.active:duration-0.cursor-pointer.select-none.active:bg-black.active:bg-opacity-10
    [:div.mr-4.shrink-0.py-2
     {:class (when (:disabled input) "opacity-50 pointer-events-none")}
     [:div.cursor-pointer.inline-block.align-middle.relative
      [:input.hidden (merge {:type "checkbox"} (dissoc input :data-testid))]
      (if (:checked input)
        [:i.flex.items-center.justify-center.text-white.dark:text-black.rounded-full.border
         {:data-testid (get input :data-testid (str "item-radio-" label))
          :class (str "w-[22px] h-[22px] " mobile/bg-primary mobile/border-primary)}
         [:div.rounded-full.bg-white.relative {:style {:width 9 :height 9 :top 0 :left 0}}]]
        [:i.flex.items-center.justify-center.text-white.dark:text-black.rounded-full.border.border-black.border-opacity-30.dark:border-white.dark:border-opacity-30
         {:data-testid (get input :data-testid (str "item-radio-" label))
          :class "w-[22px] h-[22px] "}])]]
    [:div.pr-4.w-full.relative.py-2.5.flex.flex-col.justify-center
     {:class (str "min-h-[44px] " mobile/hairline-b)}
     [:div.flex.justify-between.items-center
      [:div.shrink (when (:disabled input) {:class "opacity-50"})
       [:div {:class ["text-[12px]"]} label]]]]]])

(defn printable-german-bill-of-lading-back [{:keys [copies bill-of-lading]}]
  [:div.bg-white.max-w-4xl.mx-auto.p-12.shadow-lg.relative.print:max-w-none.print:shadow-none.print:p-0.font-narrow.min-h-a4.break-after-avoid
   [:h3.text-xs.italic "Important Instruction to Carrier/Truck Driver:"]
   [:p.italic {:class "text-[10px]"}
    "The carrier/driver hereby confirms that they have received the driver's instructions and
       will comply with them. Upon receiving the vehicles and accessories, the driver must
       check for damages and missing parts and ensure that the sender countersigns for any
       complaints. The identity of the vehicle(s) and accessories handed over was checked.
       Any complaints must be confirmed before the commencement of the journey."]
   [:h4.text-xs "Besondere Vereinbarung:"]
   [:p {:class "text-[10px]"}
    "Der Transport der Fahrzeuge findet, wenn nichts anderes vereinbart ist, in offenen,
       nicht mit Planen gedeckten Fahrzeugen statt. Erforderliche Abschreibungen und Vorbehalte
       sind konkret unter Angabe der Feststellungen und des Umfangs des voraussichtlichen Schadens
       vorzunehmen; pauschale Abschreibungen sind unzulässig. Sämtliche Angaben sind gemäß
       Schadensfeststellungsprotokoll der ARS Altmann AG genau zu dokumentieren. Widerspricht der
       Frachtführer den vom Versender geltend gemachten Ersatzansprüchen nicht schriftlich innerhalb
       einer Frist von 2 Wochen nach Haftbarhaltung, so sind jedwede Einwendungen im Hinblick auf die
       Höhe des Schadens verfallen. Bei ARS Altmann AG rechtzeitig eingegangene Meldungen gelten
       auch bei späterer Haftbarhaltung an den Frachtführer. Für später eingehende Anzeigen gilt
       § 438 HGB. Geschäftsbedingungen des Frachtführers finden keine Anwendung. Bei der Übernahme
       von Zollgut bestätigt der Empfänger mit seiner Unterschrift, es in Kenntnis seiner Eigenschaften
       übernommen und sich zur fristgerechten Gestellung beim zuständigen Zollamt verpflichtet zu haben."]
   [:h4.text-xs.italic "Special Agreement:"]
   [:p.italic {:class "text-[10px]"}
    "Unless agreed otherwise, the vehicle shall be transported in open vehicles without tarpaulin
       covers. Impairment charges and reservations must be given in concrete form, including the
       determinations and the extent of the foreseeable damage. All information must be documented
       in accordance with the damage determination report of ARS Altmann AG. If the carrier fails to
       contest in writing the sender's claims for compensation for damages within two weeks after notice
       of liability, any objections relating to the level of the damages shall be rendered void.
       Any notices received by ARS Altmann AG within time shall be deemed valid even if the carrier
       receives notice of liability later. For notices received later, the provisions of Section 438
       of the HGB (German Commercial Code) shall apply. No terms and conditions of the carrier shall
       apply. When receiving bonded goods, the recipient confirms with their signature that they have
       taken receipt of the goods with full knowledge of their characteristics and shall present them
       at the applicable customs office within the due time."]
   [:h4.text-xs "Sitz der Gesellschaft:"]
   [:p {:class "text-[10px]"} "Wolnzach, Registergericht Ingolstadt, HRB 179922."]
   [:h4.text-xs.italic "Central Office:"]
   [:p.italic {:class "text-[10px]"} "D-85283 Wolnzach, Germany. Commercial register of local court: Ingolstadt, HRB 179922."]
   [:h4.text-xs "Bei ausländischen Frachtführern:"]
   [:p {:class "text-[10px]"} "Deutsches Recht und Gerichtsstand Pfaffenhofen (BRD) sind vereinbart."]
   [:h4.text-xs.italic "For International Carriers:"]
   [:p.italic {:class "text-[10px]"}
    "It is agreed that the laws of Germany shall apply and that the place of jurisdiction shall
       be Pfaffenhofen (FRG)."]
   [:p.font-bold.mt-4 {:class "text-[10px]"}
    "Bitte beachten Sie unsere Allgemeinen Geschäftsbedingungen in der jeweils neuesten Fassung,
       die wir Ihnen auf Wunsch gerne zur Verfügung stellen und die Sie bei uns im Internet unter
       www.ars-altmann.de herunterladen können."]
   [:p.font-bold.mt-4.italic {:class "text-[10px]"}
    "Please note our general terms and conditions (AKS), latest version, which can be provided
       on demand or downloaded from our website at www.ars-altmann.de."]
   [:p.mt-4 {:class "text-[10px]"}
    (str/join " • " (map-indexed (fn [i title] (str "Blatt " (inc i) ": " (:de title))) copies))]
   [:p {:class "text-[10px]"}
    (str/join " • " (map-indexed (fn [i title] (str "Page " (inc i) ": " (:en title))) copies))]])

(defn location-text [location]
  [:span
   (when (:address/name location)
     [:<> [:span.font-bold (:address/name location)] [:br]])
   (str/join ", " (remove nil? [(:address/street location)
                                (str (:address/postcode location) " " (:address/city location))
                                (:address/province-code location)]))])

(defn printable-german-bill-of-lading-front [{:keys [title bill-of-lading]}]
  (let [{:keys [destination origin vehicles transport]} (:document/data bill-of-lading)
        transporter (:transport/transporter transport)
        carrier (or (:transport/carrier transport) (:transport/carrier transporter))]
    [:div.bg-white.max-w-4xl.mx-auto.p-12.shadow-lg.relative.print:max-w-none.print:shadow-none.print:p-0.font-narrow.min-h-a4.page-break-after
     [:div.flex.items-center.justify-between.gap-3
      [:div.text-center.flex-shrink-0
       [:div.text-sm.font-bold.uppercase.leading-tight "ARS Altmann AG"]
       [:div.uppercase.leading-tight {:class "text-[10px] tracking-[0.23rem]"} "Automobillogistik"]
       [:div {:class "text-[9px]"} "Feierabendmühle 2 • D-85283 Wolnzach"]
       [:div {:class "text-[9px]"} "www.ars-altmann.de"]]
      [:div.text-center.flex-auto.relative {:class "-top-1"}
       [:div.text-lg.uppercase.font-bold title]
       [:div {:class "text-[9px]"}
        "Zertifiziert nach DIN EN ISO 9001, 14001, 45001 und 50001" [:br]
        "Certified DIN EN ISO 9001, 14001, 45001 and 50001"]]
      [:div.flex.justify-end.flex-shrink-0
       [:img.inline-block {:src (ui.logo/logo (-> transport :origin/location :compound/name)) :width 130 :height 52}]]]
     [:div.flex.mt-3
      [:div.flex-shrink-0.flex.flex-col {:class "w-[51%]"}
       [:div.border.border-b-0.border-black.p-1
        [:div.mb-1 {:class "text-[8px]"} "Spediteur – " [:span.italic "Freight Forwarder"]]
        [:div.font-bold {:class "text-[11px]"} "ARS Altmann AG – Automobillogistik"]
        [:div {:class "text-[11px]"} "Feierabendmühle 2 • D-85283 Wolnzach"]
        [:div {:class "text-[11px] leading-tight"} "Telefon +49 8442 9080-0"]
        [:div.mt-1 {:class "text-[11px]"}
         [:span.font-bold "Schadensmeldung an: ext-reklamation@ars-altmann.de"] [:br]
         [:span.italic "Report damages to: ext-reklamation@ars-altmann.de"]]]
       [:div.border.border-b-0.border-black.p-1
        [:div.mb-1 {:class "text-[8px]"} "Absender – " [:span.italic "Sender"]]
        [:div.mt-1.font-bold {:class "text-[11px] leading-tight"}
         (-> origin :compound/company :company/name)
         [:br]
         (:address/street origin) [:br]
         (:address/postcode origin) " "
         (:address/city origin)]]
       [:div.border-2.border-b-0.border-black.p-1
        [:div.mb-1 {:class "text-[8px]"} "Auslieferungsort der Sendung – " [:span.italic "Place of delivery of the goods"]]
        [:div.font-bold {:class "text-[11px] leading-tight"}
         (:address/name destination) [:br]
         (:address/street destination) [:br]
         (:address/postcode destination) " " (:address/city destination)]]
       [:div.border-2.border-black.p-1.flex-auto
        [:div.mb-1 {:class "text-[8px]"} "Ort, Datum, Uhrzeit der Übernahme/Unterschrift Fahrer – "
         [:span.italic "Place, date and time of taking over the goods/Drivers signature"]]]]
      [:div.flex-shrink-0 {:class "w-[49%]"}
       [:div.border.border-b-0.border-black.p-1 {:class "-ml-[1px] -mr-[1px]"}
        [:div.text-xs.font-bold "Frachtbrief " [:span.italic.font-normal "Bill of lading"]]
        [:div.flex.justify-between
         [:div {:class "text-[11px]"}
          "national/international"]
         [:div.flex.gap-3
          [:div.flex.items-center.gap-1
           [:div.w-3.h-3.border.border-black]
           [:div {:class "text-[11px]"} "national"]]
          [:div.flex.items-center.gap-1
           [:div.w-3.h-3.border.border-black]
           [:div.border.border-black.px-2 {:class "text-[12px] rounded-[100%]"} "CMR"]]]]
        [:div.mt-1 {:class "text-[7px] leading-tight"}
         "Diese Beförderung unterliegt trotz einer gegenteiligen Abmachung den Bestimmungen des Übereinkommens über den Beförderungsvertrag im internationalen Straßengüterverkehr. "
         [:span.italic "This carriage is subject notwithstanding any clause to the contrary, to the Convention on the Contract for the international Carriage of goods by road (CMR)."]]]
       [:div.border.border-b-0.border-black.p-1 {:class "-ml-[1px] -mr-[1px]"}
        [:div.mb-2 {:class "text-[8px]"} "Absender Unterschrift – " [:span.italic "Sender signature"]]]
       [:div.border.border-b-0.border-black.p-1 {:class "-ml-[1px] -mr-[1px]"}
        [:div {:class "text-[8px]"} "Frachtbrief-Nr./Barcode-Nr. – " [:span.italic "Waybill/Barcode No."]]
        [:div.font-bold {:class "text-[11px] leading-normal"} (:document/number bill-of-lading)]]
       [:div.border.border-b-0.border-black.flex {:class "-ml-[1px] -mr-[1px]"}
        (let [truck-number (or (:truck/number transporter)
                               (:carrier/code carrier))]
          [:div.border-r.border-black.p-1 {:class "w-1/2"}
           [:div {:class ["text-[8px]"]} "Zug-Nr. – " [:span.italic "Truck No."]]
           (when truck-number
             [:div.font-bold {:class "text-[11px] leading-normal"} truck-number])])
        [:div.p-1 {:class "w-1/2"}
         [:div {:class "text-[8px]"} "Lot-Nr. – " [:span.italic "Reference No."]]
         (when (:load/id transport)
           [:div.font-bold {:class "text-[11px] leading-normal"} (:load/id transport)])]]
       [:div.border-2.border-b-0.border-black.p-1 {:class "-ml-[1px] -mr-[1px]"}
        [:div {:class [(when-not transporter "mb-5") "text-[8px]"]}
         "Frachtführer (vollständige Firmierung, Anschrift, Land) – " [:span.italic "Transporter (name, address, country)"]]
        (when carrier
          [:div {:class "text-[11px] leading-normal"}
           [:span
            (when (:address/name carrier)
              [:<> [:span.font-bold (:address/name carrier)] ", "])
            (when (:address/street carrier)
              (str (:address/street carrier) ", "))
            (when (:address/postcode carrier)
              (str (:address/postcode carrier) " "))
            (when (:address/city carrier)
              (str (:address/city carrier)))]])]
       [:div.border-t.border-black.border-l-2.border-r-2.flex {:class "-ml-[1px] -mr-[1px]"}
        (let [license-plate (or (:transport/license-plate transporter) (:transport/license-plate transport))]
          [:div.p-1.border-r.border-black.flex.justify-between {:class "w-1/2"}
           [:div {:class ["text-[8px]"]}
            "Amtl. Kennz. LKW – " [:br] [:span.italic "License plate no. Truck"]]
           (when license-plate
             [:div.font-bold {:class "text-[11px] leading-normal"} license-plate])])
        [:div.p-1 {:class "w-1/2"}
         [:div.mb-1 {:class "text-[8px]"} "Amtl. Kennz. Anhänger – " [:br] [:span.italic "License plate no. Trailer"]]]]
       [:div.border-t.border-black.border-l-2.border-r-2.flex {:class "-ml-[1px] -mr-[1px]"}
        [:div.p-1.border-r.border-black {:class "w-1/2"}
         [:div {:class ["text-[8px]" (when-not (:transport/driver-name transport) "mb-3")]}
          "Fahrer 1 (Name) – " [:span.italic "Driver 1 (name)"]]
         (when (:transport/driver-name transport)
           [:div.font-bold {:class "text-[11px] leading-normal"}
            (:transport/driver-name transport)])]
        [:div.p-1 {:class "w-1/2"}
         [:div.mb-3 {:class "text-[8px]"} "Fahrer 2 (Name) – " [:span.italic "Driver 2 (name)"]]]]
       [:div.border-t.border-black.border-l-2.border-r-2.border-b-2.p-1.flex {:class "-ml-[1px] -mr-[1px]"}
        [:div.mb-1 {:class "text-[8px]"} "Entladedatum – " [:span.italic "Date of Unloading"]]
        [:div.flex-auto.ml-3
         [:div.flex.justify-between
          [:div.mb-1 {:class "text-[8px] w-1/2"} "Anfang – " [:span.italic "start"]]
          [:div.mb-1 {:class "text-[8px] w-1/2"} "Zeit – " [:span.italic "time"]]]
         [:div.flex.justify-between
          [:div.mb-1 {:class "text-[8px] w-1/2"} "Ende – " [:span.italic "end"]]
          [:div.mb-1 {:class "text-[8px] w-1/2"} "Zeit – " [:span.italic "time"]]]]]]]
     [:div.p-1
      [:div {:class "text-[8px] h-12"}
       "Anweisungen an den Frachtführer/Fahrer – "
       [:span.italic "Important instruction to carrier/truck driver"]
       [:div.font-bold.mt-1.leading-normal {:class "text-[11px]"} (:location/delivery-modalities destination)]]]
     [:div.p-1.border-2.border-black
      [:div {:class "text-[8px]"}
       "Hiermit bestätigt der Frachtführer/Fahrer u.s. Fahrzeuge unbeschädigt und mit vollem Zubehör (analog Ausstattungsliste) übernommen zu haben. Abweichungen muss sich der Frachtführer/Fahrer vom Absender gegenzeichnen lassen. Weiterhin bestätigt der Frachtführer/Fahrer die relevanten Fahreranweisungen und gesetzlichen Vorgaben einzuhalten. Weiterführende rechtlich bindende Informationen sind der Rückseite zu entnehmen – " [:span.italic.font-bold "For English intruction, please turn page!"]]]
     [:table.border.border-black.w-full {:class "-mt-[1px]" :cell-spacing 0 :cell-padding 0}
      [:thead
       [:tr
        [:th.text-left.p-1.font-normal.border-r.border-black.text-right {:class "text-[8px]"}
         "Nr."
         [:div.italic "No."]]
        [:th.text-left.p-1.font-normal.border-r.border-black {:class "text-[8px]"}
         "Fahrgestell-Nr."
         [:div.italic "vehicle identification number"]]
        [:th.text-left.p-1.font-normal.border-r.border-black {:class "text-[8px]"}
         "Fabrikat/Modell"
         [:div.italic "Brand/Type"]]
        [:th.text-right.p-1.font-normal.border-r.border-black {:class "text-[8px]"}
         "Gewicht"
         [:div.italic "weight"]]
        [:th.text-left.p-1.font-normal.border-r.border-black {:class "text-[8px]"}
         "Bemerkungen/Sonderzubehör/Schadenvermerk"
         [:div.italic "Remarks/special accessories/note about claim"]]]]
      (into [:tbody {:class "text-[11px]"}]
            (map-indexed (fn [i vehicle]
                           [:tr.border-t.border-black
                            [:td.px-1.border-r.border-black.text-right.leading-tight
                             {:class "py-[1mm] w-[8mm]"}
                             (inc i)]
                            [:td.px-1.border-r.border-black.font-bold.leading-tight
                             {:class "py-[1mm]"}
                             (:vehicle/vin vehicle)]
                            [:td.px-1.border-r.border-black.font-bold.leading-tight
                             {:class "py-[1mm] text-[9px]"}
                             (or (:make/name vehicle)
                                 (get-in vehicle [:vehicle/make :make/name])) " "
                             (:vehicle/model vehicle)]
                            [:td.px-1.text-right.border-r.border-black.font-bold.leading-tight
                             {:class "py-[1mm] text-[9px]"}
                             (some-> vehicle :vehicle/weight (str "kg"))]
                            [:td.px-1.leading-tight
                             {:class "py-[1mm] text-[8px]"}
                             (str/join ", " (:vehicle/equipment vehicle))]]))
            (rpad-seq vehicles 10 []))]
     [:div.border-2.border-black.flex {:class "-mt-[1px]"}
      [:div.border-r-2.border-black.flex.flex-col.p-1 {:class "w-1/2"}
       [:div.font-bold {:class "text-[8px]"} "Empfangsbestätigung EMPFÄNGER – " [:br]
        [:span.italic "Notice of receipt CONSIGNEE"]]
       [:div.mt-1.flex.gap-1
        [:div.w-3.h-3.border.border-black.flex-shrink-0]
        [:div {:class "text-[8px]"} "Anlieferung außerhalb der Geschäftszeit (vorstehend aufgeführter Fahrzeuge und alle Fahrzeugschlüssel erhalten. Es wurde keine Kontrolle der Fahrzeuge auf Transportschäden durchgeführt): "
         [:span.italic "Delivery outside business hours (aforesaid vehicles and all car keys received. There was no control of the vehicles for transport damages):"]]]
       [:div.mt-1.flex.gap-1
        [:div.w-3.h-3.border.border-black.flex-shrink-0]
        [:div {:class "text-[8px]"} "Anlieferung innerhalb der Geschäftszeit (die Fahrzeuge wurden auf sichtbare Schäden überprüft) "
         [:span.italic "Delivery within business hours (the vehicles were controlled to visible damages)"]]]
       [:div.flex-auto]
       [:div.flex.gap-2
        [:div {:class "w-1/2"}
         [:div.border-b.border-black.mb-1 {:class "h-[10mm]"}]
         [:div {:class "text-[6px]"} "(Datum/Uhrzeit/Stempel/Unterschrift Empfänger) "
          [:span.italic "(Date/Time/Stamp/Consignee's signature)"]]]
        [:div {:class "w-1/2"}
         [:div.border-b.border-black.mb-1 {:class "h-[10mm]"}]
         [:div {:class "text-[6px]"} "(Name des Empfängers in Druckbuchstaben) "
          [:span.italic "(Name of consignee in printed characters)"]]]]]
      [:div.p-1.flex.flex-col {:class "w-1/2"}
       [:div.font-bold {:class "text-[8px]"} "Anlieferbestätigung FRACHTFÜHRER – " [:br]
        [:span.italic "Notice of delivery FREIGHT FORWARDER/DRIVER"]]
       [:div.mt-1.flex.gap-1
        [:div.w-3.h-3.border.border-black.flex-shrink-0]
        [:div
         [:div {:class "text-[8px]"} "Anlieferung außerhalb der Geschäftszeit (ohne Kontrolle der Fahrzeuge durch Empfänger) "
          [:span.italic "Delivery outside business hours (without control of the vehicles from consignee)"]]
         [:div {:class "text-[8px]"} "Wo worden die Schlüssel hinterlegt? (Bei Anlieferung außerhalb der Geschäftszeiten): "
          [:span.italic "Where are the keys deposited? (Delivery outside business hours)"]]]]
       [:div.border-b.border-black {:class "h-[6mm]"}]
       [:div.mt-1.flex.gap-1
        [:div.w-3.h-3.border.border-black.flex-shrink-0]
        [:div {:class "text-[8px]"} "Anlieferung innerhalb der Geschäftszeit (mit Fahrzeugkontrolle durch Empfänger) "
         [:span.italic "Delivery within business hours (with control of the vehicles from consignee)"]]]
       [:div.flex.gap-2.flex-auto
        [:div {:class "w-1/2"}
         [:div.border-b.border-black.mb-1 {:class "h-[6mm]"}]
         [:div {:class "text-[6px]"} "(Datum/Uhrzeit/Stempel/Unterschrift Fahrer) "
          [:span.italic "(Date/Time/Stamp/Driver's signature)"]]]
        [:div {:class "w-1/2"}
         [:div.border-b.border-black.mb-1 {:class "h-[6mm]"}]
         [:div {:class "text-[6px]"} "(Name des Fahrers in Druckbuchstaben) "
          [:span.italic "(Name of Driver in printed characters)"]]]]]]]))

(defn printable-german-bill-of-lading [opts]
  (let [copies [{:de "Absender" :en "Sender"}
                {:de "Frachtführer" :en "Carrier"}
                {:de "Frachtführer (Abrechnung)" :en "Carrier (Accounting)"}
                {:de "Empfänger" :en "Consignee"}]]
    [:div.not-prose
     [:div.mx-auto.flex.flex-col.gap-6.print:block.break-after-avoid
      (into [:<>]
            (map (fn [title]
                   [:<>
                    [printable-german-bill-of-lading-front (assoc opts :title (:de title))]
                    [printable-german-bill-of-lading-back (assoc opts :copies copies)]]))
            copies)]]))

(defn entry-transports-table [{:as args :keys [transports]}]
  [:div.bg-white.rounded-lg.border.border-slate-300.shadow-sm.font-sans.text-sm.not-prose.overflow-x-auto
   [:table.w-full
    [:thead
     [:tr.border-b
      #_[:th {:class (tw-class :th ["text-right"])} (tr "Status")]
      [:th {:class (tw-class :th ["border-l"])} (tr "Transport")]
      [:th {:class (tw-class :th ["border-l"])} (tr :entry/datetime)]
      [:th {:class (tw-class :th ["border-l"])} (tr :transport/loading-line)]
      [:th {:class (tw-class :th ["border-l"])} (tr "Progress")]
      [:th {:class (tw-class :th ["border-l" "text-right"])} (tr "Vehicle count")]
      [:th {:class (tw-class :th ["border-l"])} (tr "Actions")]]]
    (into [:tbody]
          (map (partial entry-transports-table-row args))
          (->> transports
               (sort-by :entry/datetime util/compare-with-nil-last)
               (reverse)))]])

(def table-schema
  [:transport/status ; TODO fix tr'ing status keyword in filter omnibox
   :load/id
   :transport/waypoints
   :transport/earliest-delivery-date
   :transport/progress
   :transport/vehicle-count
   :ductile/created-at
   :transport/finalized-at
   :transport/ready-at
   :transport/mode
   :transport/contract-names
   :transport/forwarder
   :transport/load-number
   :transport/customer-load-number
   :transport/carrier
   :truck/number
   :transport/loading-line
   :transport/license-plate
   :transport/driver-name
   :transport/created-by
   :transport/dispatcher])

(defn filter-loads [filters loads]
  (let [filter-fns (keep (fn [[k filter-val]]
                           (cond
                             (= k :transport/waypoints)
                             (fn [transport]
                               (let [waypoints-str (str/join (remove str/blank? (into [(when (:self-pickup? transport) (tr :self-pickup))
                                                                                       (when-not (:self-pickup? transport) (tr :own-transport))]
                                                                                      (map :address/city (:waypoints transport)))))]
                                 (omnibox/matches? waypoints-str
                                                   (map (fn [filter] (update filter :filter.omnibox/type
                                                                             #(if (and (= :filter.omnibox.type/exact %)
                                                                                       (not= (tr :self-pickup) (:filter.omnibox/label filter)))
                                                                                :filter.omnibox.type/substring
                                                                                %)))
                                                        filter-val))))

                             (and (seqable? filter-val) (some :filter.omnibox/type filter-val))
                             #(omnibox/matches? (get % k) filter-val)

                             :else
                             (when-not (empty? filter-val)
                               #(contains? filter-val (get % k)))))
                         filters)]
    (cond->> loads
      (not-empty filter-fns)
      (filterv (apply every-pred filter-fns)))))


(defn transports-table [{:as args :keys [schema actions transform-columns row-action selected-rows transports]}]
  [:div.flex.flex-col.gap-20.min-w-full.max-h-full
   (let [col-settings @(deframe/!localstorage-get :ductile.ui.transport/col-settings)
         hide-columns [:transport/finalized-at
                       :transport/ready-at]
         available-cols (into (set schema) hide-columns)

         !expanded-at (ui/use-localstorage ::loads-table-expanded-at {})
         !opts (ui/use-localstorage ::loads-table-opts
                                    {:hide-columns hide-columns
                                     :schema schema
                                     :column-order []}
                                    (fn reset-when [stored]
                                      (not= (set schema)
                                            (set (:schema stored)))))
         !sorting (ui/use-localstorage ::loads-table-sorting [:transport/progress :desc])
         columns-profile (columns/->table-opts {:col-settings col-settings
                                                :available-cols available-cols
                                                :default-selected-cols schema})
         transports (into []
                          (map (fn [transport]
                                 (let [sp? (self-pickup? transport)
                                       origin (:origin/location transport)
                                       waypoints (if (contains? transport :waypoints)
                                                   (:waypoints transport)
                                                   (when-not sp? (make-waypoints (assoc args :transport transport))))]
                                   (-> transport
                                       (update :transport/forwarder :address/name)
                                       (update :transport/mode tr)
                                       (update :transport/contract-names #(str/join ", " (sort %)))
                                       (assoc :transport/vehicle-count (-> transport :transport/progress :total))
                                       (assoc :origin (:origin/location transport))
                                       (assoc :self-pickup? sp?)
                                       (assoc :waypoints waypoints)))))
                          transports)
         filtered-transports (filter-loads (select-keys (:filters @!expanded-at) ;; only apply filters of visible cols
                                                        (:select-columns columns-profile)) transports)]
     [:div.overflow-auto.text-xs {:style {:max-height "calc(100vh - 118px)"}}
      [table/table
       (merge
        {:row-key :ductile/id
         :row-action row-action
         :selected-rows selected-rows
         :th-class "text-wrap text-left w-full inline-block pl-1 pr-2 text-[12px]"
         :tr-class ""
         :td-class "text-[12px] py-1.5"
         :transports (vec transports)
         :!expanded-at !expanded-at
         :!sorting !sorting
         :filters (into {:transport/waypoints [:omnibox (into [(tr :self-pickup)
                                                               (tr :own-transport)]
                                                              (sort (set (remove str/blank?
                                                                                 (mapcat (fn [t]
                                                                                           (map :address/city (:waypoints t)))
                                                                                         transports)))))]}
                        (for [[k opts] [[:transport/status {}]
                                        [:transport/name {}]
                                        [:truck/number {}]
                                        [:load/id {}]
                                        [:transport/mode {}]
                                        [:transport/contract-names {}]
                                        [:transport/forwarder {}]
                                        [:transport/vehicle-count {:numeric? true}]
                                        [:transport/load-number {}]
                                        [:transport/customer-load-number {}]
                                        [:transport/license-plate {}]
                                        [:transport/driver-name {}]
                                        [:transport/dispatcher {}]]]
                          [k [:omnibox
                              (into (sorted-set)
                                    (keep #(let [v (if (vector? k)
                                                     (get-in % k)
                                                     (get % k))
                                                 choice (cond
                                                          (keyword? v) (tr v)
                                                          (number? v) v
                                                          :else (str v))]
                                             (when-not (str/blank? (str choice))
                                               choice)))
                                    transports)
                              opts]]))

         :column-layout {:transport/status {:min-width 60}}
         :sorting-columns {[:load/id] (fn [s] (mapv #(try (-> %
                                                              (str/replace #"^0+" "")
                                                              edn/read-string)
                                                          (catch :default _ %))
                                                    (mapcat #(str/split % #"/")
                                                            (str/split s #"-"))))
                           [:transport/progress]
                           (fn [{:keys [done total]}]
                             (int (* 100 (/ done total))))}
         :compare-columns {[:entry/datetime] util/compare-with-nil-last
                           [:exit/datetime] util/compare-with-nil-last
                           [:arrival/datetime] util/compare-with-nil-last}
         :transform-columns (cond-> (merge {[:transport/name]
                                            (fn [transport]
                                              [:a.underline {:href (transport-link transport)}
                                               (:transport/name transport)])

                                            [:transport/waypoints]
                                            (fn [{:as transport :keys [origin waypoints]}]
                                              (if (self-pickup? transport)
                                                {:labels [:self-pickup]}
                                                transport))

                                            [:load/id]
                                            (fn [transport]
                                              [:a.underline {:data-testid (str "load-id-" (:load/id transport))
                                                             :href (transport-link transport)}
                                               (:load/id transport)])

                                            [:transport/carrier]
                                            (fn [transport]
                                              (or (-> transport :transport/carrier :company/name)
                                                  (-> transport :transport/carrier :address/name)))

                                            [:transport/earliest-delivery-date]
                                            (fn [transport]
                                              {:format :datetime
                                               :style :short
                                               :locale (locale/get-current-locale)
                                               :instant (:transport/earliest-delivery-date transport)})


                                            [:transport/loading-line]
                                            (fn [transport]
                                              (str (-> transport :transport/loading-line :zone)
                                                   (-> transport :transport/loading-line :row)))

                                            [:transport/created-by]
                                            (fn [transport]
                                              (-> transport :ductile/user :user/email))

                                            [:transport/finalized-at]
                                            (fn [transport]
                                              {:format :datetime
                                               :style :short
                                               :locale (locale/get-current-locale)
                                               :instant (:transport/finalized-at transport)})

                                            [:transport/ready-at]
                                            (fn [transport]
                                              {:format :datetime
                                               :style :short
                                               :locale (locale/get-current-locale)
                                               :instant (:transport/ready-at transport)})
                                            [:ductile/created-at]
                                            (fn [transport]
                                              {:format :datetime
                                               :style :short
                                               :locale (locale/get-current-locale)
                                               :instant (:ductile/created-at transport)})

                                            [:transport/progress]
                                            (fn [transport]
                                              [vehicle-count-badge transport])}
                                           transform-columns)
                              actions (assoc [:actions]
                                             actions))}
        @!opts
        columns-profile)
       filtered-transports]


      (when @(deframe/!route-get :ductile.ui.transport/col-settings-modal?)
        [columns/select-columns-modal
         {:col-settings col-settings
          :available-cols available-cols
          :default-selected-cols schema
          :on-close #(deframe/route-dissoc! :ductile.ui.transport/col-settings-modal?)
          :on-change (fn [new-col-settings]
                       (deframe/localstorage-assoc! :ductile.ui.transport/col-settings new-col-settings)
                       (deframe/route-dissoc! :ductile.ui.transport/col-settings-modal?))}])])])

(def dropdown-menu-styles
  {:content "min-w-[220px] bg-white rounded-md p-[5px] shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),_0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)] will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade"
   :checkbox-item "group text-[13px] leading-none text-indigo-700 rounded-[3px] flex items-center h-[25px] px-[5px] relative pl-[25px] select-none outline-none data-[disabled]:text-mauve8 data-[disabled]:pointer-events-none data-[highlighted]:bg-indigo-500 data-[highlighted]:text-white"
   :item-indicator "absolute left-0 w-[25px] inline-flex items-center justify-center"})

(defn tab-nav [tabs & more]
  (let [active-classes ["flex" "px-3" "py-2" "rounded-t" "border" "border-[#bdc7d4]" "border-b-0" "font-bold"]
        idle-classes ["flex" "px-3" "py-2" "rounded-t" "border" "border-[#bdc7d4]" "border-b-0" "bg-[#dcdfe4]" "hover:bg-slate-100"]]
    [:div.border-b.flex.w-full.justify-between
     {:class ["border-[#bdc7d4]"]}
     (into [:ul.flex.text-sm]
           (map (fn [{:keys [label template icon]}]
                  (if (= (router/path template (:path-params @deframe/!current-route))
                         (.. js/window -location -pathname))
                    [:li.relative
                     [:a.flex.gap-1.items-center
                      {:href (router/path template (:path-params @deframe/!current-route))
                       :class active-classes
                       :style {:box-shadow "inset 0 4px 4px rgba(255,255,255,.25)"}}
                      (when icon
                        [ui/icon {:size 12} icon])
                      label]
                     [:div.border-b.border-gray-200.absolute.left-0 {:class ["bottom-[-1px]" "right-[1px]"]}]]
                    [:li
                     {:class "-ml-[1px]"}
                     [:a.transition.flex.gap-1.items-center
                      {:href (router/path template (:path-params @deframe/!current-route))
                       :class idle-classes
                       :style {:background-image "linear-gradient(transparent 78%, rgba(205, 208, 212, 0.75))"}}
                      (when icon
                        [ui/icon {:size 16} icon])
                      label]])))
           tabs)
     (into [:div.flex.justify-end.gap-3]
           more)]))

(defn transports-tabs [& more]
  (into
   [tab-nav
    [{:label (tr "Planned Loads")
      :template '[compound :compound-name transports]}
     {:label (tr "Entry Loads")
      :template '[compound :compound-name entry-transports]}
     {:label (tr "Exited Loads")
      :template '[compound :compound-name exited-transports]}
     {:label (tr "Search Loads")
      :icon :magnifying-glass
      :template '[compound :compound-name loads-search]}]
    [columns/select-profile-dropdown {:col-settings @(deframe/!localstorage-get :ductile.ui.transport/col-settings)
                                      :on-change #(deframe/localstorage-assoc! :ductile.ui.transport/col-settings %)
                                      :on-modal-open #(deframe/route-assoc! :ductile.ui.transport/col-settings-modal? true)}]]
   more))

(defn with-actions [args]
  (assoc args
         :schema (conj table-schema :transport/name :actions)
         :actions (fn [transport]
                    (let [link-opts (into {:compound-name (:compound/name args)} transport)]
                      [:<>
                       [:a.text-indigo-600.hover:underline.mr-3
                        {:class "text-[11px]"
                         :href (transport-link link-opts)}
                        "Details"]
                       [:a.text-indigo-600.hover:underline
                        {:class "text-[11px]"
                         :href (transport-link 'load-list link-opts)}
                        "Load List"]]))))

(defn export-handler [compound-name]
  (let [col-settings @(deframe/!localstorage-get :ductile.ui.transport/col-settings)
        columns-profile (columns/->table-opts {:col-settings col-settings
                                               :available-cols (set table-schema)
                                               :default-selected-cols table-schema})]
    (http/download "/api/lambda"
                   {:lambda/handlers '[ductile.transport/export-xlsx
                                       ductile.spreadsheet/export-xlsx]
                    :compound/name compound-name
                    :show-in-planning? false
                    :column-order (:column-order columns-profile)})))

(defn export-button [compound-name]
  (let [export-running? (hooks/use-state false)]
    [ui/button
     {:disabled? @export-running?
      :size 2
      :variant :primary
      :on-click (fn [_]
                  (reset! export-running? true)
                  (-> (export-handler compound-name)
                      (.then #(reset! export-running? false))))}
     icons/excel
     (tr "Export")]))

(defn overview [args _match]
  [:div
   [:div.flex.justify-between.items-center.mb-2
    [transports-tabs]
    [:f> export-button (:compound/name args)]]
   (when (:compound/enforce-loading-area-assignment? (compound-config/get-compound-config (:compound/name args)))
     (when-current-user-scope
      #{:scope/train-entry :scope/inspect-workflow}
      (misplaced-fahrzeuge-in-lines {:transports (:transports args)
                                     :all-fahrzeuge (:all-fahrzeuge args)})))
   [:div.border.border-slate-300.rounded.overflow-hidden
    [transports-table (with-actions args)]]])

(defn entry-overview [args match]
  [:div
   [:div.flex.justify-between.items-center.mb-2
    [transports-tabs
     (when-current-user-scope
      :scope/gate-control
      [:a.text-gray-600.hover:text-gray-700.active:text-gray-800.flex.items-center.gap-1
       {:href (router/path '[compound :compound-name gate entry new] (-> match :path-params))}
       [:span.flex.items-center.text-xs.gap-1
        [ui/icon {:size 16} :plus] (tr "Record Entry Transport")]])]]
   [entry-transports-table args]])

(defn loads-search [args match]
  (let [[search-key query] @(deframe/!route-get ::loads-search [:load/id ""])]
    [:div
     [:div.flex.justify-between.items-center.mb-2
      [transports-tabs]]
     [:div.flex
      [:form.flex.rounded-md.bg-slate-100.p-2.border.border-slate-300.items-center.gap-1.mb-6
       {:on-submit (fn [event]
                     (.preventDefault event)
                     (deframe/call-lambda 'ductile.transport/loads-search {search-key query
                                                                           :compound/name (get-in @deframe/!current-route [:path-params :compound-name])}))}
       [:div.flex
        [ui/select {:size 3
                    :choices [{:ui/value :load/id
                               :ui/label (tr :load/id)}
                              {:ui/value :transport/customer-load-number
                               :ui/label (tr :transport/customer-load-number)}
                              {:ui/value :transport/name
                               :ui/label (tr :transport/name)}]
                    :on-change (fn [selected-value]
                                 (deframe/route-assoc! ::loads-search [selected-value query]))
                    :value search-key}]]
       [:div.flex
        [ui/input {:size 3
                   :class ["w-[400px]"]
                   :placeholder (case search-key
                                  :load/id (tr "e.g. LL-EIT-123")
                                  :transport/customer-load-number (tr "e.g. 534590")
                                  :transport/name (tr "e.g. HANKO"))
                   :on-change (fn [e] (deframe/route-assoc! ::loads-search [search-key (.. e -target -value)]))}]]
       [:div.flex
        [ui/button {:variant :primary
                    :loading? @deframe/!loading?
                    :size 3}
         (tr "Search")]]]]
     [transports-table (with-actions args)]]))

(defn mode-icon
  ([transport] [mode-icon {} transport])
  ([options transport]
   [ui/icon
    options
    (case (:transport/mode transport)
      :mode/ship :anchor
      :mode/truck :truck
      :mode/train :train
      :mode/own-wheels :car
      :help-circle)]))

(defn $edit-transport [load]
  (let [serializable-load (-> load
                              (assoc :destination/ductile-ids (mapv :ductile/id (:waypoints load)))
                              (dissoc :waypoints))]
    (-> (http/$lambda
         'ductile.transport/handle-edit-transport
         serializable-load)
        (.then (fn [result]
                 (if (:error result)
                   (deframe/tx! [[:op/assoc-in [:current-route/transient ::error] result]])
                   (deframe/tx! [[:op/assoc-in [:current-route/transient ::transport] result]
                                 [:op/dissoc-in [:current-route/transient :edit-transport-form]]
                                 [:op/assoc :toast {:title (str "✅ " (tr "Transport changes saved."))
                                                    :description (tr "Your changes to %1 are now available." (:load/id load))
                                                    :variant :success}]])))))))

(defn find-truck-by-truck-number [trucks truck-number]
  (some (fn [truck]
          (when (= truck-number (:truck/number truck))
            truck))
        trucks))

(rf/reg-sub
 ::edit-params
 (fn [_]
   [(deframe/!route-get :edit-transport-form)
    (deframe/!get-in [:trucks :result])])
 (fn [[form trucks] [_q-id]]
   (if (:truck/number form)
     (merge (apply dissoc form ui/carrier-attrs)
            (first (filter (comp #{(:truck/number form)} :truck/number) trucks)))
     form)))

(defn carriers-overview [_result match]
  [:div
   [:div.mb-4.flex.items-center.justify-between
    [:h2.text-2xl.mx-2 (tr "Carriers")]
    (when-current-user-write-scope
     :scope/carriers
     [ui/button
      {:variant :primary
       :href (router/path '[compound :compound/name carriers new] (:path-params match))}
      [ui/icon :plus]
      (tr "Add carrier")])]
   [ui/table+
    [ui/table-header
     [ui/table-row
      [ui/table-header-cell (tr "Address name")]
      [ui/table-header-cell (tr "Carrier code")]
      [ui/table-header-cell (tr "Street")]
      [ui/table-header-cell (tr "Postcode")]
      [ui/table-header-cell (tr "City")]
      [ui/table-header-cell (tr "Country")]
      [ui/table-header-cell (tr "Tax Number")]
      [ui/table-header-cell (tr "VAT-ID")]
      [ui/table-header-cell (tr "Actions")]]]
    (into [ui/table-body]
          (map (fn [carrier]
                 [ui/table-row
                  [ui/table-cell (:address/name carrier)]
                  [ui/table-cell (:carrier/code carrier)]
                  [ui/table-cell (:address/street carrier)]
                  [ui/table-cell (:address/postcode carrier)]
                  [ui/table-cell (:address/city carrier)]
                  [ui/table-cell (-> carrier :address/country :country/name)]
                  [ui/table-cell (:customer/tax-number carrier)]
                  [ui/table-cell (:customer/tax-id carrier)]
                  [ui/table-cell
                   (when-current-user-write-scope
                    :scope/carriers
                    (when-not (:external.pegaso/supplier-id carrier)
                       ;; Pegaso carrier is managed by pegaso and pushed via API
                      [ui/link
                       {:href (router/path '[compound :compound/name carriers :ductile/id edit]
                                           (merge (:path-params match)
                                                  {:ductile/id (:ductile/id carrier)}))}
                       (tr "Edit carrier")]))]]))
          @(deframe/!route-get-in [:fetch :result]))]])

(defn truck-inactive? [truck]
  (= :transporter.status/inactive (:transporter/status truck)))

(defn trucks-overview [trucks _match]
  [:div
   [:div.mb-4.flex.items-center.justify-between
    [:h2.text-2xl.mx-2 (tr "Trucks")]
    (when-current-user-write-scope
     :scope/trucks
     [ui/button
      {:variant :primary
       :href (router/path '[trucks new])}
      [ui/icon :plus]
      (tr "Add truck")])]
   [table/table
    {:schema [:truck/number
              :transport/license-plate
              :transport/carrier
              :truck/valid-from
              :truck/valid-until
              :transporter/status
              :actions]
     :transform-columns
     {[:truck/number] (fn [truck]
                        (if (truck-inactive? truck)
                          [:span.line-through.text-gray-500 (:truck/number truck)]
                          (:truck/number truck)))
      [:transport/carrier] (fn [truck]
                             (:address/name (:transport/carrier truck) ui/unassigned))
      [:transporter/status] (fn [truck]
                              (tr (:transporter/status truck)))
      [:actions] (fn [truck]
                   (when-current-user-write-scope
                    :scope/trucks
                    [:div.flex.gap-2
                     [ui/link
                      {:href (router/path '[trucks :ductile/id edit] {:ductile/id (:ductile/id truck)})}
                      (tr "Edit truck")]]))}}
    trucks]])

(defn transport-data-list [{:as args :keys [transport]}]
  (let [selected-carrier (or (get-in transport [:transport/transporter :transport/carrier])
                             (get-in transport [:transport/carrier]))
        editable? (and (scope-auth/authorized-for? (:user/read-scopes @deframe/!current-user) #{:scope/train-entry :scope/inspect-workflow})
                       (not (italian-compound? (:compound/name args))))
        entry-transport? (entry-transport? args)]
    [ui/data-list
     [ui/data-list-item
      [ui/data-list-key (tr "Transport mode")]
      [ui/data-list-value
       (if-let [mode (:transport/mode transport)]
         [:span.flex.items-center.gap-1
          [mode-icon {:size 16} transport]
          [:span.flex.items-center.gap-2
           (tr (str/capitalize (str/replace (name mode) #"-" " ")))
           (when (and (self-pickup? transport) (not entry-transport?))
             [dispo-badge (tr "self pickup")])]]
         [ui/data-list-empty-value (tr "Unassigned")])]]
     [ui/data-list-item
      [ui/data-list-key (tr "Status")]
      [ui/data-list-value [ui/transport-status-badge (:transport/status transport)]]]
     [ui/data-list-item
      [ui/data-list-key (tr "Load ID")]
      [ui/data-list-value {:data-testid "load-id"}
       (:load/id transport [ui/data-list-empty-value (tr "Unassigned")])]]
     (when (:transport/customer-load-number transport)
       [ui/data-list-item
        [ui/data-list-key (tr :transport/customer-load-number)]
        [ui/data-list-value {:data-testid "customer-load-number"}
         (:transport/customer-load-number transport)]])
     [ui/data-list-item
      [ui/data-list-key (tr :transport/dispatcher)]
      [ui/data-list-value (:transport/dispatcher transport)]]
     [ui/data-list-item
      [ui/data-list-key (tr :transport/created-by)]
      [ui/data-list-value (-> transport :ductile/user :user/email)]]
     [ui/data-list-item
      [ui/data-list-key (tr "Created at")]
      [ui/data-list-value (time-format/->str {:format :datetime :locale :de} (:ductile/created-at transport))]]
     (when (:entry/datetime transport)
       [ui/data-list-item
        [ui/data-list-key (tr "Entered at")]
        [ui/data-list-value (time-format/->str {:format :datetime :locale :de} (:entry/datetime transport))]])
     (when (:transport/finalized-at transport)
       [ui/data-list-item
        [ui/data-list-key (tr "Finalized at")]
        [ui/data-list-value (time-format/->str {:format :datetime :locale :de} (:transport/finalized-at transport))]])
     (when (:transport/preparable-at transport)
       [ui/data-list-item
        [ui/data-list-key (tr "Preparable at")]
        [ui/data-list-value (time-format/->str {:format :datetime :locale :de} (:transport/preparable-at transport))]])
     (when (:transport/ready-at transport)
       [ui/data-list-item
        [ui/data-list-key (tr "Ready at")]
        [ui/data-list-value (time-format/->str {:format :datetime :locale :de} (:transport/ready-at transport))]])
     (when (:transport/earliest-delivery-date transport)
       [ui/data-list-item
        [ui/data-list-key (tr :transport/earliest-delivery-date)]
        [ui/data-list-value (time-format/->str {:format :datetime :locale :de} (:transport/earliest-delivery-date transport))]])
     (when (:transport/scheduled-customer-delivery-datetime transport)
       [ui/data-list-item
        [ui/data-list-key (tr :transport/scheduled-customer-delivery-datetime)]
        [ui/data-list-value (:transport/scheduled-customer-delivery-datetime transport)]])
     (when (:exit/datetime transport)
       [ui/data-list-item
        [ui/data-list-key (tr "Exited at")]
        [ui/data-list-value (time-format/->str {:format :datetime :locale :de} (:exit/datetime transport))]])
     [ui/data-list-item
      [ui/data-list-key (tr "Loading line")]
      [ui/data-list-value (if-let [{:keys [row zone]} (:transport/loading-line transport)]
                            [:span {:data-testid "loading-line-label"} (str zone row)]
                            [ui/data-list-empty-value (tr "Unassigned")])]]
     [ui/data-list-separator (tr "Transport details")]
     (when (:transport/name transport)
       [ui/data-list-item
        [ui/data-list-key (tr "Transport name")]
        [ui/data-list-value {:data-testid "transport-name"}
         (:transport/name transport)]])
     [ui/data-list-item
      [ui/data-list-key (tr "Driver")]
      [ui/data-list-value {:data-testid "driver"}
       (:transport/driver-name transport [ui/data-list-empty-value (tr "Unassigned")])]]
     [ui/data-list-item {:variant :align-top}
      [ui/data-list-key
       [:div {:class (when editable? "pt-[4px]")}
        (tr "Truck number")]]
      [ui/data-list-value {:data-testid "truck-number"}
       (:truck/number transport [ui/data-list-empty-value (tr "Unassigned")])]]
     [ui/data-list-item
      [ui/data-list-key (tr "License plate")]
      [ui/data-list-value {:data-testid "license-plate"}
       (:transport/license-plate transport [ui/data-list-empty-value (tr "Unassigned")])]]
     [ui/data-list-item
      [ui/data-list-key (tr "Note")]
      [ui/data-list-value {:data-testid "transport-note"}
       (:ductile/note transport [ui/data-list-empty-value (tr "Unassigned")])]]
     (when-current-user-scope
      :scope/transport-customer-price
      (when (:transport/customer-negotiated-amount transport)
        [ui/data-list-item
         [ui/data-list-key (tr "Negotiated price (customer)")]
         [ui/data-list-value {:data-testid "transport-customer-negotiated-amount"}
          (util/format-price (:transport/customer-negotiated-amount transport))]]))
     (when (:transport/carrier-negotiated-amount transport)
       [ui/data-list-item
        [ui/data-list-key (tr "Negotiated price (carrier)")]
        [ui/data-list-value {:data-testid "transport-negotiated-amount"}
         (util/format-price (:transport/carrier-negotiated-amount transport))]])
     [ui/data-list-separator (tr "Carrier")]
     [ui/data-list-item
      [ui/data-list-key (tr "Name")]
      [ui/data-list-value
       (:address/name selected-carrier [ui/data-list-empty-value (tr "Unassigned")])]]
     [ui/data-list-item
      [ui/data-list-key (tr "Street")]
      [ui/data-list-value
       (:address/street selected-carrier [ui/data-list-empty-value (tr "Unassigned")])]]
     [ui/data-list-item
      [ui/data-list-key (tr "Postcode")]
      [ui/data-list-value
       (:address/postcode selected-carrier [ui/data-list-empty-value (tr "Unassigned")])]]
     [ui/data-list-item
      [ui/data-list-key (tr "City")]
      [ui/data-list-value
       (:address/city selected-carrier [ui/data-list-empty-value (tr "Unassigned")])]]]))

(defn transport-status-form [{:as args :keys [transport line loading-lines]} waypoints]
  (let [{:keys [zone row]} @(deframe/!route-get ::line)
        {:transport/keys [available-actions status loading-line mode preparable-at]} transport
        entry-transport? (= (:compound/name args)
                            (-> transport :destination/location :compound/name))
        rows (->> (cond-> loading-lines
                    loading-line
                    (conj loading-line))
                  (filter #(= (:zone %) zone))
                  (map :row)
                  (remove nil?)
                  set)]
    (hooks/use-effect #(deframe/route-assoc! ::line loading-line))
    [:div
     [:<>
      [:h4.font-bold.mb-2.mt-6 (tr "Status")]

      (when (:transport/can-be-custom-cleared? available-actions)
        [:div.overflow-x-auto.mt-3
         [:table.w-full.text-left.text-sm
          [:thead
           [:tr.text-mauve11 {:class "text-[10px] md:text-[12px]"}
            [:th.align-bottom.leading-tight.pb-1 (tr :fahrzeug/customs-clearance)]
            [:th.align-bottom.leading-tight.pb-1 (tr "Can be requested")]
            [:th.align-bottom.leading-tight.pb-1 (tr "Requested")]
            [:th.align-bottom.leading-tight.pb-1 (tr "Cleared")]
            [:th.align-bottom.leading-tight.pb-1 (tr "Actions")]]]
          [:tbody {:class "text-[11px] md:text-sm"}
           [:tr.border-t.border-slate7.odd:bg-mauve2
            [:td.align-top.font-bold {:class "py-0.5"} "H2"]
            [:td.align-top {:class "py-0.5"} (:h2-requestable args)]
            [:td.align-top {:class "py-0.5"} (:h2-request args)]
            [:td.align-top {:class "py-0.5"} (:h2 args)]
            [:td.align-top {:class "py-0.5"} [ui/button
                                              {:size 1
                                               :class "mr-2"
                                               :data-testid "request-h2"
                                               :disabled? (or @(deframe/!route-get ::h2-requested?)
                                                              @(deframe/!route-get ::h1-requested?)
                                                              (zero? (:h2-requestable args)))
                                               :on-click (fn [_e]
                                                           (deframe/route-assoc! ::h2-requested? true)
                                                           (.then
                                                            (http/$lambda
                                                             'ductile.transport/request-customs-clearance!
                                                             {:ductile/id (:ductile/id transport)
                                                              :fahrzeug/customs-clearance :document.kind/h2})
                                                            (fn [result]
                                                              (if (:error result)
                                                                (deframe/route-assoc! ::error result)
                                                                (deframe/assoc! :toast {:title (str "✅ " (tr "H2 has been requested"))
                                                                                        :description (tr "The H2 has been requested")
                                                                                        :variant :success})))))}
                                              [ui/icon {:size 16} :shopping-bag]
                                              (tr "Request H2")]]]
           [:tr.border-t.border-slate7.odd:bg-mauve2
            [:td.align-top.font-bold {:class "py-0.5"} "H1"]
            [:td.align-top {:class "py-0.5"} (:h1-requestable args)]
            [:td.align-top {:class "py-0.5"} (:h1-request args)]
            [:td.align-top {:class "py-0.5"} (:h1 args)]
            [:td.align-top {:class "py-0.5"} [ui/button
                                              {:size 1
                                               :data-testid "request-h1"
                                               :disabled? (or @(deframe/!route-get ::h1-requested?)
                                                              (zero? (:h1-requestable args)))
                                               :on-click (fn [_e]
                                                           (deframe/route-assoc! ::h1-requested? true)
                                                           (.then
                                                            (http/$lambda
                                                             'ductile.transport/request-customs-clearance!
                                                             {:ductile/id (:ductile/id transport)
                                                              :fahrzeug/customs-clearance :document.kind/h1})
                                                            (fn [result]
                                                              (if (:error result)
                                                                (deframe/route-assoc! ::error result)
                                                                (deframe/assoc! :toast {:title (str "✅ " (tr "H1 has been requested"))
                                                                                        :description (tr "The H1 has been requested")
                                                                                        :variant :success})))))}
                                              [ui/icon {:size 16} :shopping-bag]
                                              (tr "Request H1")]]]]]])
      (when (:transport/can-be-marked-entered? available-actions)
        [:div.flex.items-center.gap-2.mt-3
         [ui/icon :arrow-right]
         [ui/button
          {:size 1
           :class "ml-3"
           :data-testid "mark-entered"
           :disabled? (:entry/datetime transport)
           :on-click (fn [_e] (.then
                               (http/$lambda
                                'ductile.transport/mark-entered!
                                {:ductile/id (:ductile/id transport)})
                               (fn [result]
                                 (if (:error result)
                                   (deframe/route-assoc! ::error result)
                                   (deframe/route-refetch!)))))}
          [ui/icon {:width 16 :height 16} :exit]
          (tr "Mark as entered")]])
      (when (:transport/can-set-loading-line? available-actions)
        [:div.flex.items-center.gap-2.mt-3
         [ui/icon :arrow-right]
         [:div.bg-slate-50.p-3.rounded-md
          [:div.flex.items-center.gap-2
           [:div.w-40 {:data-testid "select-zone"}
            [:label.text-xs.font-bold.mb-1 (tr "Zone")
             [:span.text-slate-500.ml-1 "(" (tr "optional") ")"]]
            [combobox/view {:choices (conj (map (fn [zone]
                                                  {:ui/label zone
                                                   :ui/value zone})
                                                (set (map :zone loading-lines)))
                                           {:ui/label (tr "Unassigned")})

                            :value (if zone
                                     {:ui/label zone
                                      :ui/value zone}
                                     {:ui/label (tr "Unassigned")})
                            :on-change (fn [value]
                                         (deframe/tx! [[:op/assoc-in [:current-route/transient ::line :zone] (:ui/value value)]
                                                       [:op/dissoc-in [:current-route/transient ::line :row]]]))}]]
           [:div.w-40 {:data-testid "select-row"}
            [:label.text-xs.font-bold.mb-1 (tr "Row")
             [:span.text-slate-500.ml-1 "(" (tr "optional") ")"]]
            [combobox/view {:choices (conj (map (fn [row]
                                                  {:ui/label row
                                                   :ui/value row})
                                                (sort rows))
                                           {:ui/label (tr "Unassigned")})

                            :value (if row
                                     {:ui/label row :ui/value row}
                                     {:ui/label (tr "Unassigned")})
                            :on-change (fn [value]
                                         (deframe/tx! [[:op/assoc-in [:current-route/transient ::line :row] (:ui/value value)]]))}]]]

          [:div.flex.items-center.gap-2.mt-2
           [ui/button
            {:size 1
             :data-testid "toggle-preparable"
             :on-click (fn [_e] (.then
                                 (http/$lambda
                                  'ductile.transport/toggle-preparable!
                                  (cond-> {:ductile/id (:ductile/id transport)}
                                    zone (assoc :parking-zone/name zone)
                                    (and zone row) (assoc :parking-spot/row row)))
                                 (fn [result]
                                   (if (:error result)
                                     (deframe/route-assoc! ::error result)
                                     (do
                                       (deframe/call-route-refetch)
                                       (deframe/tx! [[:op/assoc-in [:current-route/transient ::transport] result]
                                                     [:op/dissoc-in [:current-route/transient ::error]]]))))))}
            [ui/icon {:size 16} :truck]
            (if preparable-at
              (tr "Update loading line")
              (tr "Mark as ready for preparation"))]
           (when preparable-at
             [ui/button
              {:size 1
               :data-testid "mark-not-preparable"
               :variant :destructive
               :on-click (fn [_e] (.then
                                   (http/$lambda
                                    'ductile.transport/toggle-preparable!
                                    (cond-> {:ductile/id (:ductile/id transport)
                                             :transport/loading-line-retract? true}))
                                   (fn [result]
                                     (if (:error result)
                                       (deframe/route-assoc! ::error result)
                                       (deframe/tx! [[:op/dissoc-in [:current-route/transient ::line :row]]
                                                     [:op/dissoc-in [:current-route/transient ::line :zone]]
                                                     [:op/assoc-in [:current-route/transient ::transport] result]
                                                     [:op/dissoc-in [:current-route/transient ::error]]])))))}
              (tr "Mark as not ready for preparation")])]]])
      (when (:transport/can-be-ready-for-pickup? available-actions)
        [:div.flex.items-center.gap-2.mt-3
         [ui/icon :arrow-right]
         [ui/button
          {:size 1
           :class "ml-3"
           :data-testid "toggle-ready-for-pickup"
           :disabled? (and
                       (not= :transport.status/ready-for-pickup status)
                       (not= :transport.status/fully-prepared status))
           :on-click (fn [_e] (.then
                               (http/$lambda
                                'ductile.transport/toggle-load-ready!
                                {:ductile/id (:ductile/id transport)})
                               (fn [result]
                                 (if (:error result)
                                   (deframe/route-assoc! ::error result)
                                   (deframe/tx! [[:op/assoc-in [:current-route/transient ::transport] result]
                                                 [:op/dissoc-in [:current-route/transient ::error]]])))))}
          [ui/icon {:size 16} :truck]
          (if (= :transport.status/ready-for-pickup status)
            (tr "Mark as not ready for pickup")
            (tr "Mark as ready for pickup"))]])
      (when (:transport/can-be-marked-exited? available-actions)
        [:div.flex.items-center.gap-2.mt-3
         [ui/icon :arrow-right]
         [ui/button
          {:size 1
           :class "ml-3"
           :data-testid "mark-exited"
           :disabled? (or (:exit/datetime transport)
                          (and entry-transport? (not (:entry/datetime transport)))
                          (and (not (#{:mode/ship :mode/train} mode))
                               (not (self-pickup? transport))
                               (or (not (every? (comp :document :waypoint/bill-of-lading) waypoints))
                                   (not= :transport.status/ready-for-pickup status))))
           :on-click (fn [_e] (.then
                               (http/$lambda
                                'ductile.transport/mark-exited!
                                {:ductile/id (:ductile/id transport)})
                               (fn [result]
                                 (if (:error result)
                                   (deframe/route-assoc! ::error result)
                                   (deframe/route-refetch!)))))}
          [ui/icon {:width 16 :height 16} :exit]
          (tr "Mark as exited")]])]]))

(defn show-no-route-details-available! [waypoints]
  (deframe/route-assoc! ::route {:details (vec (repeat (count waypoints) {:message (tr "No route details available.")}))}))

(defn js->route-data [^js js-route ^js js-waypoints]
  {:line (j/lit {:type "FeatureCollection"
                 :features [{:type "Feature"
                             :properties {}
                             :geometry (j/get js-route :geometry)}]})
   :stops (j/lit {:type "FeatureCollection"
                  :features (.map js-waypoints
                                  (fn [waypoint index]
                                    (j/lit {:type "Feature"
                                            :geometry {:type "Point"
                                                       :coordinates (j/get waypoint :location)}
                                            :properties {:index (or ;; for optimized route, use index attribute
                                                                 (j/get waypoint :waypoint_index)
                                                                    ;; for directions route, the list index is the order
                                                                 index)}})))})
   :details (vec (js->clj (j/get js-route :legs) {:keywordize-keys true}))})

(defn update-waypoints+order! [transport waypoints]
  (deframe/route-assoc! ::waypoints waypoints)
  ;; update ordering in backend
  ($edit-transport (assoc transport :waypoints waypoints)))

(defn set-route-to-optimized-result! [route-key res]
  (let [route (j/get-in res [:trips 0])
        waypoints (j/get res :waypoints)]
    (deframe/route-assoc! route-key (js->route-data route waypoints))
    {:route route :waypoints waypoints}))

(defn fetch-route! [{:keys [origin waypoints optimize? transport]}]
  (deframe/route-assoc! ::fetching-route? true)
  (.. (mapbox-route (waypoint-coord origin)
                    (map waypoint-coord waypoints)
                    optimize?)
      (then (fn [^js res]
              (deframe/route-dissoc! ::fetching-route?)
              (if (= "Ok" (.-code res))
                (if optimize?
                  (let [{js-origin+waypoints :waypoints} (set-route-to-optimized-result! ::route res)
                        reordering (into {} (rest (.map js-origin+waypoints
                                                        (fn [w index]
                                                          ;; `dec` to exclude origin from indice calcs
                                                          [(dec index) (dec (j/get w :waypoint_index))]))))
                        updated-waypoints (map (fn [i] (nth waypoints (get reordering i i)))
                                               (range (count waypoints)))]
                    (when (not= waypoints updated-waypoints)
                      (update-waypoints+order! transport updated-waypoints)))
                  (deframe/route-assoc! ::route (js->route-data (j/get-in res [:routes 0])
                                                                (j/get-in res [:routes 0 :waypoints]))))
                (show-no-route-details-available! waypoints))))))

(defn waypoint-vehicle-list [{:as waypoint :waypoint/keys [editable?]}]
  (let [size (:layout/size waypoint 2)]
    (into [:ul.list-style-none]
          (map
           (fn [{:as transport :vehicle/keys [model vin]}]
             (let [binding-appointment? (and (:fahrzeug/priorities transport)
                                             (contains? (set (:fahrzeug/priorities transport)) :prioritiy/binding-customer-appointment))]
               [:li
                {:class (concat [(case size
                                   2 "text-[15px]"
                                   1 "text-[11px]")]
                                (case (:layout/variant waypoint)
                                  :separated ["border-b" "last:border-b-0" "py-1"]
                                  ["mt-1"]))}
                [:div.flex
                 {:class (case size
                           1 ["gap-1"]
                           2 ["gap-2"])}
                 (when editable?
                   [:div.text-indigo-700.hover:text-red-600.cursor-pointer
                    {:class "w-[12px] h-[12px] mt-[4px]"
                     :on-click #(when-let [remove-vehicle! (:waypoint/remove-vehicle! waypoint)]
                                  (.stopPropagation %)
                                  (.preventDefault %)
                                  (remove-vehicle! transport))}
                    [ui/icon {:size 12} :trash]])
                 (when (:fahrzeug/expected-customer-delivery-datetime transport)
                   [:div {:class "mt-[1px]"}
                    [ductile.clerk.render/priority {:size 1} {:priority/date (:fahrzeug/expected-customer-delivery-datetime transport)}]])
                 [:div
                  {:class (when (and (:layout/show-equipment? waypoint)
                                     (:fahrzeug/expected-customer-delivery-datetime transport))
                            ["pl-2"])}
                  [:div.flex.gap-1
                   (when binding-appointment?
                     [:div.rounded-sm.bg-yellow-200.leading-none.inline-flex.items-center.whitespace-nowrap
                      {:class (concat ["px-[3px]"]
                                      (case size
                                        1 ["text-[10px]" "h-[13px]"]
                                        2 ["text-[15px]" "h-[17px]"]))}
                      (str "🤝 " (time-format/->str {:format :date :style :short}
                                                    (:fahrzeug/expected-customer-delivery-datetime transport)))])
                   [:div.flex.items-center.gap-1.flex-wrap
                    [:span.font-bold.whitespace-nowrap (util/str-join-existing " " (:make/name transport) model)]
                    [:span.text-slate-600
                     (if (:compound/name waypoint)
                       [ui/link {:href (router/path '[compound :compound-name vehicle :fahrzeug-id]
                                                    {:compound-name (:compound/name waypoint)
                                                     :fahrzeug-id vin})} [ui/vin vin]]
                       [ui/vin vin])]
                    (when (and (:parking-zone/name transport) (:layout/show-parking? waypoint))
                      [:div.text-slate-700.flex.gap-1.items-center
                       [ui/icon {:size 12} :sewing-pin]
                       (tr "Parked at") " " (parking-utils/parking-spot->str transport)])]]
                  [:div
                   (when (and (:vehicle/equipment transport) (:layout/show-equipment? waypoint))
                     [:div.text-slate-700.flex.gap-1.items-center
                      [ui/icon {:size 12} :backpack]
                      (str/join ", " (:vehicle/equipment transport))])]]]])))
          (:waypoint/transports waypoint))))

(defn waypoint-index-icon [waypoint]
  [:div.rounded-full.flex.justify-center.items-center.flex-shrink-0.font-bold
   {:class "w-[24px] h-[24px] bg-red-100 text-[red] border-[red] border-[1px] shadow"}
   (if (:waypoint/origin? waypoint)
     [ui/icon {:size 12} :home]
     (inc (:waypoint/index waypoint)))])

(defn waypoint-loading-type-indicator [waypoint]
  [:div.flex.items-center.gap-1
   [:div.border-t-2.flex-auto]
   [:div.border-2.px-2.p-1.rounded-full.bg-white.shrink-0
    (if (:waypoint/origin? waypoint)
      [tr "Starting point"]
      [tr "Stop %1" (inc (:waypoint/index waypoint))])]
   [:div.border-t-2.flex-auto]])

(defn route-waypoint [{:as waypoint :waypoint/keys [editable? index last? origin? sortable?]}]
  [:div.bg-mauve2.p-3.text-xs.border-b.last:border-b-0
   {:class (when-not origin? "hover:bg-mauve3")}
   [:div.flex.gap-2
    [:div {:class "w-[24px]"}
     [waypoint-index-icon waypoint]]
    [:div.flex-auto {:class (when origin? "opacity-60")}
     [:div.flex.w-full.items-center.justify-between
      {:class "text-[11px] mt-[3px] ml-[1px]"}
      (when (:layout/show-loading-type? waypoint true)
        [:div.font-bold
         [waypoint-loading-type-indicator waypoint]])]
     [:div.flex.gap-1
      (when (and editable? (not= editable? :vehicles))
        [:div.text-indigo-700.hover:text-red-600.cursor-pointer
         {:class "w-[12px] h-[14px] mt-[3px]"
          :on-click #(tx! [[:op/assoc-in [:current-route/transient ::confirm-removing-waypoint] waypoint]])}
         [ui/icon {:size 12} :trash]])
      [:div
       [:div.font-bold {:class "text-[14px]"} (str (:address/postcode waypoint) " " (:address/city waypoint))]
       [:div {:class "text-[12px] mt-[3px]"} (:address/name waypoint)]
       (when (:layout/show-street? waypoint)
         [:div {:class "text-[12px]"} (:address/street waypoint)])]]
     [:div.mt-2
      [waypoint-vehicle-list (merge {:layout/size 1} waypoint)]]
     (when-let [{:keys [document generate!]} (and (:layout/show-bill-of-lading? waypoint) (:waypoint/bill-of-lading waypoint))]
       [:div.flex.items-center.gap-2.mt-2
        {:class "text-[11px]"}
        [:span.font-bold (str (tr "bill of lading") ":")]
        (when document [ui/link {:href (routes/path '[compound :compound-name bill-of-lading :ductile-id]
                                                    {:ductile-id (:ductile/id document)
                                                     :compound-name (:compound/name waypoint)})}
                        [ui/icon {:width 12 :height 12} :file]
                        (:document/number document)])
        (when-current-user-scope
         :scope/bill-of-lading
         [ui/link {:on-click generate! :variant :destructive}
          [ui/icon {:width 12 :height 12} :update]
          (if document (tr "Regenerate") (tr "Generate"))])])
     (when-not origin?
       [:div {:class ["text-[11px]"
                      "text-slate10"
                      (if (and (:layout/show-bill-of-lading? waypoint)
                               (:waypoint/bill-of-lading waypoint))
                        "mt-1"
                        "mt-2")]}
        (if-let [{:keys [distance duration message]} (:waypoint/details waypoint)]
          (or message (str (tr "Distance") ": " (util/format-distance distance) ", "
                           (tr "Duration") ": " (util/format-duration duration)))
          (str (tr "Requesting details") "…"))
        (when (and (:location/delivery-modalities waypoint)
                   (:layout/show-delivery-modalities? waypoint)
                   (not origin?))
          (into [:div.mt-1.leading-normal]
                (interpose [:br] (str/split-lines (:location/delivery-modalities waypoint)))))])]
    (when (and (not origin?) sortable? editable?)
      [:div.bg-white.rounded-sm.border.flex.items-center.justify-center.cursor-move.flex-shrink-0.hover:border-indigo-300.hover:text-indigo-700
       {:class "w-[24px] h-[24px] sort-handle"}
       [ui/icon :drag-handle-horizontal]])]])

(defn waypoints-list [{:as opts :keys [waypoints/editable? layout/show-origin?]} {:as args :keys [transport]}]
  (let [!el (hooks/use-ref nil)
        origin (make-origin transport)
        waypoints (or @!waypoints (make-waypoints args))
        sortable? (and editable? (< 1 (count waypoints)))
        waypoint-views (map-indexed (fn [i waypoint]
                                      ^{:key (str "waypoint-" i)}
                                      [route-waypoint
                                       (set/rename-keys (merge waypoint
                                                               opts
                                                               {:waypoint/index i
                                                                :waypoint/editable? editable?
                                                                :waypoint/sortable? sortable?
                                                                :waypoint/details (get-in @!route [:details i])
                                                                :layout/show-bill-of-lading? true
                                                                :layout/show-street? true
                                                                :layout/show-loading-type? true
                                                                :layout/show-equipment? true
                                                                :layout/show-parking? true
                                                                :layout/show-delivery-modalities? true})
                                                        {:waypoint/fahrzeuge :waypoint/transports})])
                                    waypoints)]
    (hooks/use-effect #(when (and (nil? @!route) (seq waypoints))
                         (if (every? :location/latitude waypoints)
                           (fetch-route! {:origin origin
                                          :waypoints waypoints
                                          :optimize? false
                                          :transport transport})
                           (show-no-route-details-available! waypoints)))
                      [waypoints])
    (hooks/use-effect
     (fn []
       (when (and @!el sortable?)
         (let [sortable-instance (Sortable @!el #js {:forcePlaceholderSize true})]
           (.addEventListener
            (first sortable-instance)
            "sortupdate"
            #(let [updated-waypoints (util/vec-move waypoints
                                                    (.. % -detail -origin -index)
                                                    (.. % -detail -destination -index))]
               (update-waypoints+order! transport updated-waypoints)
               ;; update route (without optimizing route/changing stop order)
               (fetch-route! {:origin @!origin
                              :waypoints updated-waypoints
                              :optimize? false
                              :transport transport}))))))
     [@!el waypoints])
    [:<>
     (when show-origin?
       [route-waypoint
        (set/rename-keys (merge origin
                                opts
                                (select-keys args [:compound/name])
                                {:waypoint/origin? true
                                 :waypoint/editable? (when (not= :transport.status/exited (:transport/status transport))
                                                       :vehicles)
                                 :waypoint/remove-vehicle! (fn [vehicle]
                                                             (.then
                                                              (http/$lambda
                                                               'ductile.transport/remove-vehicle
                                                               {:ductile/id (:ductile/id vehicle)
                                                                :phase :exit})
                                                              (fn [{:as result :keys [vehicle/vin]}]
                                                                (if (:error result)
                                                                  (deframe/route-assoc! ::error result)
                                                                  (deframe/route-assoc! ::transport result)))))
                                 :layout/show-street? true
                                 :layout/show-loading-type? true
                                 :layout/show-equipment? true
                                 :layout/show-parking? true})
                         {:waypoint/fahrzeuge :waypoint/transports})])
     (when-not (self-pickup? transport)
       ^{:key waypoints} [:div {:ref !el} (doall waypoint-views)])]))

(defn str-join-existing [separator & values]
  (str/join separator (remove nil? (into [] values))))

(defn exit-verified? [transport]
  (contains? #{:transport.status/exited} (:transport/status transport)))

(defn mobile-waypoint [waypoint]
  [mobile/table {:title [:div
                         [:div.text-slate-600.font-bold.mb-1.leading-snug.mb-2
                          {:class "text-[12px]"}
                          [waypoint-loading-type-indicator waypoint]]
                         [:div
                          [:div.font-bold (str (:address/postcode waypoint) " " (:address/city waypoint))]
                          [:div.font-normal.mt-1.leading-snug {:class "text-[12px] "}
                           (str (:address/name waypoint) ", " (:address/street waypoint))]]
                         (when (:location/delivery-modalities waypoint)
                           [:div.flex.gap-3.mt-2.items-center
                            [:div.text-slate11
                             [ui/icon {:width 24 :height 24} :exclamation-triangle]]
                            [:div.font-medium.leading-snug {:class "text-[11px] text-slate11"}
                             (:location/delivery-modalities waypoint)]])
                         (when (and (exit-verified? (:waypoint/transport waypoint))
                                    (not (every? :fahrzeug/exit-delivery-datetime (:waypoint/fahrzeuge waypoint))))
                           [:div.mt-3
                            [ui/button
                             {:size 1
                              :class ["w-full"]
                              :on-click #(deframe/call-lambda 'ductile.transport/handle-mark-all-unloaded
                                                              {:ductile/id (-> waypoint :waypoint/transport :ductile/id)
                                                               :fahrzeug/ids (mapv :ductile/id (:waypoint/fahrzeuge waypoint))
                                                               :transport/action :unloaded})}
                             [ui/icon :check]
                             (tr "Mark all vehicles as delivered")]])]
                 :items (mapv (fn [fahrzeug]
                                [mobile/list-item
                                 {:title [:div.mr-1 {:class ["text-[11px]" "leading-tight"]}
                                          (str-join-existing " " (:make/name fahrzeug) (:vehicle/model fahrzeug))]
                                  :description [:span {:class ["text-[10px]"]} (:vehicle/vin fahrzeug)]
                                  :right (if (exit-verified? (:waypoint/transport waypoint))
                                           (if (:fahrzeug/exit-delivery-datetime fahrzeug)
                                             [:div.text-center {:class ["text-[10px]" "max-w-[100px]"]}
                                              (tr "delivered at %1" (time-format/->str {:format :datetime :locale :de}
                                                                                       (:fahrzeug/exit-delivery-datetime fahrzeug)))]
                                             [ui/button
                                              {:size 0
                                               :on-click #(deframe/call-lambda 'ductile.transport/handle-mark-unloaded
                                                                               {:ductile/id (-> waypoint :waypoint/transport :ductile/id)
                                                                                :fahrzeug/id (:ductile/id fahrzeug)
                                                                                :transport/action :unloaded})}
                                              [ui/icon :check]
                                              (tr "Deliver")])
                                           [:span.flex-shrink-0.flex.items-center
                                            {:class ["text-[10px]" "gap-1"]}
                                            [ui/spinner {:size 12}]
                                            (tr "awaiting exit")])}])
                              (:waypoint/fahrzeuge waypoint))}])

(defn transport-action-text [waypoint]
  [:div.gap-2.flex.items-center
   (case (:waypoint/action waypoint)
     :loading [ui/icon :enter]
     :unloading [ui/icon :exit])
   [:span
    (count (:waypoint/fahrzeuge waypoint)) " "
    (case (:waypoint/action waypoint)
      :loading (tr "vehicles are being loaded")
      :unloading (tr "vehicles are being unloaded"))]])

(defn waypoint-summary [address]
  [:<>
   [:div.font-bold (str (:address/postcode address) " " (:address/city address))]
   [:div.font-normal.mt-1.leading-snug.text-sm
    (str (:address/name address) ", " (:address/street address))]
   (when (:location/delivery-modalities address)
     [:div.font-medium.leading-snug.mt-1.text-slate11 {:class "text-[11px]"}
      (:location/delivery-modalities address)])])

(defn transport-ops-waypoint [waypoint]
  [:a.rounded-lg.bg-white.mx-3.overflow-hidden.block
   {:href (if (:waypoint/bill-of-lading waypoint)
            (routes/path '[transport-ops :transport-id confirm-delivery :ductile-id]
                         {:transport-id (-> waypoint :waypoint/transport :ductile/id)
                          :ductile-id (-> waypoint :waypoint/bill-of-lading :document :ductile/id)})
            (routes/path '[transport-ops :ductile-id confirm-loading]
                         {:ductile-id (-> waypoint :waypoint/transport :ductile/id)}))
    :class (when (:waypoint/active? waypoint)
             ["outline" "outline-indigo-700" "outline-offset-1"])}
   [:div.flex.items-center.gap-3
    [:div.px-3.py-2
     [waypoint-summary waypoint]]
    [:div.flex.items-center.text-indigo-600.pr-2
     [ui/icon {:width 24 :height 24} :chevron-right]]]
   [:div.border-t.px-3.py-2.text-xs
    [transport-action-text waypoint]]
   (when (:waypoint/active? waypoint)
     [:div.border-t.px-3.py-2.flex.items-center.gap-2.bg-yellow-300.border-yellow-400.font-bold
      [ui/icon :exclamation-triangle]
      [:span.text-xs
       (tr "Action required!")]])])

(defn transport-ops-segment [waypoint]
  [:div.relative.pb-6 {:style {:padding-left 40}}
   [:div.rounded-full.absolute.font-bold.justify-center.items-center.flex.leading-none.left-3.top-1.border-2
    {:style {:width 30 :height 30 :z-index 2}
     :class (if (:waypoint/active? waypoint)
              ["bg-indigo-500" "border-indigo-700" "text-white"]
              ["bg-indigo-200" "border-indigo-300"])}
    (or (:waypoint/index waypoint)
        [ui/icon {:width 12 :height 12} :star-filled])]
   (when-not (:waypoint/last? waypoint)
     [:div.h-full.bg-indigo-300.absolute
      {:style {:left 25 :top 5 :width 4 :z-index 1}}])
   [transport-ops-waypoint waypoint]])

(defn signature [{:keys [label]}]
  (let [!canvas-container (hooks/use-ref nil)]
    (hooks/use-effect
     (fn []
       (when-let [el @!canvas-container]
         (let [rect (.getBoundingClientRect el)
               canvas (.querySelector el "canvas")]
           (js/console.log canvas (str (.-width rect) "px"))
           (set! (.. canvas -style -width) (str (.-width rect) "px"))
           (signature-pad. canvas))))
     [@!canvas-container])
    [:div.w-full.rounded.border.border-slate-300.shadow-inner.relative
     {:ref !canvas-container}
     [:canvas {:height 100}]
     [:div.absolute.border-t.border-slate-300.border-dashed.left-0.right-0.pointer-events-none {:class "bottom-[24px]"}]
     [:div.text-xs.py-1.px-3 label]]))

(defn action-required [text]
  [:div.mx-3.border.border-yellow-400.bg-yellow-300.px-3.py-2.mt-3.rounded-lg
   [:div.flex.gap-2
    [:div {:class "mt-[5px]"}
     [ui/icon {:width 16 :height 16} :exclamation-triangle]]
    [:div
     [:span.font-bold.text-sm (tr "Action required!")]
     [:div.text-xs text]]]])

(defn transport-ops-vehicle [fahrzeug]
  [:div.px-3.py-2.border-t.first:border-t-0
   [:div.flex.text-sm.gap-2
    [:span.font-bold (:vehicle/model fahrzeug)]
    [:span (:vehicle/vin fahrzeug)]]
   [:div.text-xs.mt-1 (str/join ", " (:vehicle/equipment fahrzeug))]
   [:div.flex.items-center.mt-1
    {:class (if (:vehicle/weight fahrzeug) "justify-between" "justify-start")
     :on-click #(deframe/route-assoc! ::damage-modal {:fahrzeug-id (:vehicle/vin fahrzeug)})}
    [:span.text-xs.font-bold.text-indigo-600.flex.items-center.gap-1
     [ui/icon {:size 12} :damage]
     (str (tr "Report damages") "…")]
    (when (:vehicle/weight fahrzeug)
      [:span.text-xs (:vehicle/weight fahrzeug) "kg"])]])

(defn sign-here [{:keys [title description signature-label]}]
  [:div.bg-white.rounded-lg.mt-6.mx-3
   [:div.font-bold.px-3.pt-2.text-sm.flex.items-center.gap-2
    [:div.text-base "✍️"]
    title]
   [:div.text-xs.py-2.px-3
    description]
   [:div.px-3.pb-3
    [signature {:label signature-label}]]])

(defn legal-entity-text [address]
  (str/join ", "
            [(:address/name address)
             (:address/street address)
             (str (:address/postcode address) " " (:address/city address))]))

(defn damage-modal []
  (let [args @(deframe/!route-get ::damage-modal)
        !animation-complete? (hooks/use-state false)]
    [:> (.-div motion)
     {:initial {:y "100%"}
      :animate {:y 0}
      :exit {:y "100%"}
      :transition {:ease "easeOut" :duration 0.2}
      :onAnimationComplete #(reset! !animation-complete? true)
      :style {:z-index 20 :height "100vh"}
      :class-name "bg-slate-200 fixed top-0 left-0 w-screen h-screen overflow-hidden"}
     [mobile/scroll-content
      [mobile/navbar (merge {:title (tr "Report Damage")
                             :right-item [:div.flex.items-center.h-full.pl-1.text-indigo-600
                                          {:on-click #(deframe/route-dissoc! ::damage-modal)}
                                          [ui/icon {:size 20} :x]]})]
      (when @!animation-complete?
        [:iframe.absolute.w-full.h-full.left-0
         {:style {:top 43}
          :src (str "/report-damages/" (:fahrzeug-id args))}])]]))

(defn record-damage-button [args & content]
  (into
   [:div.flex.items-center.pr-1.text-indigo-600.flex-shrink-0.h-full
    {:on-click #(deframe/route-assoc! ::damage-modal args)}
    content]))

(defn navbar-damage-button [args]
  [record-damage-button args
   [:div.flex.items-center.pr-1.text-indigo-600.flex-shrink-0.h-full
    [ui/icon {:size 28} :damage]]])

(defn confirm-delivery [{:as args :keys [bill-of-lading]} _match]
  (let [default-text-class ["text-[12px]" "leading-normal"]
        {:keys [customer destination origin vehicles transport]} (:document/data bill-of-lading)]
    [:<>
     [mobile/scroll-content
      [mobile/navbar {:title (str (tr "Confirm delivery"))
                      :back [mobile/back-link {:title "Overview"
                                               :route (routes/path '[transport-ops :ductile-id]
                                                                   {:ductile-id (:transport-id args)})}]
                      :right-item [navbar-damage-button {:transport-id (:ductile/id transport)
                                                         :fahrzeug-id (-> vehicles first :vehicle/vin)}]
                      :subtitle (str (:address/postcode destination) " " (:address/city destination))}]
      [:div.bg-white.px-3.py-2.mx-3.mt-3.rounded-lg
       [waypoint-summary destination]]
      [action-required (tr "Please confirm the unloading of vehicles with your signature.")]
      [:div.mx-3.mt-6.text-sm.px-3.font-bold.text-slate-500
       [transport-action-text {:waypoint/fahrzeuge vehicles
                               :waypoint/action :unloading}]]
      [:div.bg-white.rounded-lg.mx-3.mt-3
       (into [:<>]
             (map (fn [fahrzeug]
                    [transport-ops-vehicle fahrzeug]))
             vehicles)]
      [sign-here {:title (tr "Driver - Please sign here:")
                  :description (tr "I hereby confirm the unloading of the above %1 vehicles on behalf of %2"
                                   (count vehicles)
                                   (legal-entity-text (-> transport :transport/transporter :transport/carrier)))
                  :signature-label (str (tr "Driver")
                                        (when (:driver/name transport)
                                          (str " (" (:driver/name transport) ")")))}]
      [sign-here {:title (tr "Customer – Please sign here:")
                  :description (tr "I hereby confirm the delivery of the above %1 vehicles on behalf of %2"
                                   (count vehicles)
                                   (:customer/name customer))
                  :signature-label (str (tr "Customer")
                                        " (" (:customer/name customer) ")")}]
      [:div.mx-3.mt-3
       [mobile/button-full (tr "Confirm unloading and delivery")]]]
     [:> AnimatePresence
      (when @(deframe/!route-get ::damage-modal)
        [damage-modal])]]))

(defn bill-of-lading [{:as args :keys [transport]} _match]
  ;; TODO: maybe use country code instead
  (if (italian-compound? (:compound/name args))
    [italian-bill-of-lading-view args]
    [ui/tabs {:default-value :print}
     [:div.flex.items-center.justify-between.mb-3.text-xs.w-full.max-w-4xl.mx-auto.print:hidden
      #_[ui/link
         {:disabled? true
          :href "" #_(transport-link (merge transport {:compound-name (:compound/name args)}))}
         [ui/icon :arrow-left]
         (tr "Back to transport")]
      #_[ui/tabs-list
         [ui/tab {:value :print} (tr "Print Preview")]
         [ui/tab {:value :mobile} (tr "Mobile Preview")]]]
     [ui/tab-content {:value :print :class "py-2 print:p-0 print-margin min-h-a4"}
      [printable-german-bill-of-lading args]]
     [ui/tab-content {:value :mobile :class "py-2 print:hidden"}
      [:div.flex.justify-center
       [mobile/iphone-portrait
        [confirm-delivery (assoc args :layout/variant :simulate-device)]]]]]))

(defn confirm-loading [{:as args :keys [transport]} _match]
  [:<>
   [mobile/scroll-content
    [mobile/navbar {:title (str (tr "Confirm loading"))
                    :back [mobile/back-link {:title "Overview"
                                             :route (routes/path '[transport-ops :ductile-id]
                                                                 {:ductile-id (:ductile/id transport)})}]
                    :right-item [navbar-damage-button {:transport-id (:ductile/id transport)
                                                       :fahrzeug-id (-> transport :transport/fahrzeuge first :ductile/id)}]
                    :subtitle (str (count (:transport/fahrzeuge transport)) " " (tr "Vehicles"))}]
    [action-required (tr "Please confirm the unloading of vehicles with your signature.")]
    [:div.mx-3.mt-6.text-sm.px-3.font-bold.text-slate-500
     [transport-action-text {:waypoint/fahrzeuge (:transport/fahrzeuge transport)
                             :waypoint/action :loading}]]
    [:div.bg-white.rounded-lg.mx-3.mt-3
     (into [:<>]
           (map (fn [fahrzeug]
                  [transport-ops-vehicle fahrzeug]))
           (:transport/fahrzeuge transport))]
    [sign-here {:title (tr "Driver - Please sign here:")
                :description (tr "I hereby confirm the loading of the above %1 vehicles on behalf of %2"
                                 (count (:transport/fahrzeuge transport))
                                 (legal-entity-text (-> transport :transport/carrier)))
                :signature-label (str (tr "Driver")
                                      (when (:driver/name transport)
                                        (str " (" (:driver/name transport) ")")))}]
    [:div.mx-3.mt-3
     [mobile/button-full (tr "Confirm loading of vehicles")]]]
   [:> AnimatePresence
    (when @(deframe/!route-get ::damage-modal)
      [damage-modal])]])

(defn transport-ops [{:as args :keys [transport]} _match]
  (let [origin (make-origin transport)
        waypoints (make-waypoints args)]
    [:<>
     [mobile/scroll-content
      [mobile/navbar {:title (str (tr "Transport"))
                      :back [:div.flex.items-center.pl-1.flex-shrink-0.h-full.text-indigo-600
                             [ui/icon :globe]
                             [:span.ml-1 "DE"] [ui/icon :chevron-down]]
                      :right-item [navbar-damage-button {:transport-id (:ductile/id transport)
                                                         :fahrzeug-id (-> waypoints first :waypoint/fahrzeuge first :ductile/id)}]
                      :subtitle (str (:load/id transport)
                                     (when (and (:load/id transport) (:load-number transport)) " / ")
                                     (when (:load-number transport) (str "#" (:load-number transport)))
                                     (when (:truck/number transport)
                                       (str " (Zug " (:truck/number transport) ")")))}]
      [:div.mt-6
       [transport-ops-segment (assoc origin
                                     :waypoint/active? true
                                     :waypoint/action :loading)]]
      (into [:<>]
            (map-indexed
             (fn [i waypoint]
               [transport-ops-segment (assoc waypoint
                                             :waypoint/action :unloading
                                             :waypoint/index (inc i)
                                             :waypoint/last? (= (dec (count waypoints)) i))]))
            waypoints)]
     [:> AnimatePresence
      (when @(deframe/!route-get ::damage-modal)
        [damage-modal])]]))

(defn route-metrics [route]
  (reduce (fn [acc {:keys [distance duration]}]
            (-> acc
                (update :distance + distance)
                (update :duration + duration)))
          {}
          (:details route)))

(defn mobile-route-plan-content [{:as args :keys [transport]}]
  (let [waypoints (make-waypoints args)
        origin (make-origin transport)
        load-id (:load/id transport)
        load-number (:load-number transport)
        {:keys [distance]} (route-metrics @!route)]
    (hooks/use-effect #(when-not @!route
                         (fetch-route! {:origin origin
                                        :waypoints waypoints
                                        :optimize? false
                                        :transport transport})))
    [mobile/scroll-content
     (merge {} (select-keys args [:layout/variant]))
     [mobile/navbar (merge {:title (str (tr "Route plan"))
                            :subtitle (str load-id
                                           (when (and load-id load-number) " / ")
                                           (when load-number (str "#" load-number))
                                           (when (:truck/number transport)
                                             (str " (Zug " (:truck/number transport) ")")))}
                           (select-keys args [:layout/variant]))]
     [:div.px-4.py-3.font-bold.text-center
      {:class (str mobile/bg-bars mobile/translucent mobile/hairline-b "text-[12px]")}
      [route-preview origin waypoints]]
     [:div {:class ["text-[11px]"]}
      (when (:transport/customer-load-number transport)
        [:div.bg-white.flex.justify-between.items-center.px-4.py-1.border-b.border-gray-300
         [:div (tr :transport/customer-load-number)]
         [:div (:transport/customer-load-number transport)]])
      (when (:transport/earliest-delivery-date transport)
        [:div.bg-white.flex.justify-between.items-center.px-4.py-1.border-b.border-gray-300
         [:div (tr :transport/earliest-delivery-date)]
         [:div (time-format/->str
                {:format :datetime :style :short :locale (locale/get-current-locale)}
                (:transport/earliest-delivery-date transport))]])
      (when distance
        [:div.bg-white.flex.justify-between.items-center.px-4.py-1.border-b.border-gray-300
         [:div (tr "Total distance")]
         [:div (util/format-distance distance)]])
      (when (:ductile/note transport)
        [:div.bg-white.flex.justify-between.items-center.px-4.py-1.border-b.border-gray-300
         [:div (tr :ductile/note)]
         [:div (:ductile/note transport)]])]
     #_[mobile-waypoint (assoc origin :waypoint/origin? true)]
     (into [:<>]
           (map-indexed (fn [i waypoint] [mobile-waypoint (assoc waypoint :waypoint/index i)]))
           waypoints)]))

(defn printable-route-plan [{:as args :keys [transport]}]
  (let [waypoints (make-waypoints args)
        origin (make-origin transport)
        load-id (:load/id transport)
        load-number (:load-number transport)
        {:keys [distance]} (route-metrics @!route)]
    (hooks/use-effect #(when-not @!route
                         (fetch-route! {:origin origin
                                        :waypoints waypoints
                                        :optimize? false
                                        :transport transport})))
    [:div.not-prose.border.border-transparent ; transparent border fixes blank last page
     [:div.mx-auto
      [:div.bg-white.max-w-4xl.mx-auto.p-12.shadow-lg.relative.print:max-w-none.print:shadow-none.print:p-0.page-break-avoid
       {:class "screen:min-h-[68rem] print:min-h-auto"}
       [:div.my-4.flex.justify-between.items-end
        [:div
         [:div.text-lg.font-bold.mt-0.mb-1
          (tr "Route plan") " " (tr "for") " " load-id
          (when (:transport/customer-load-number transport)
            [:div.text-sm.font-bold.text-slate11
             (tr :transport/customer-load-number) ": "
             (:transport/customer-load-number transport)])
          (when (:truck/number transport)
            [:span.text-slate11 " (" "Zug" " " (:truck/number transport) ")"])
          (when (and load-id load-number) " / ")
          (when load-number (str "#" load-number))]
         [:div.text-sm.font-bold.text-slate11 [route-preview origin waypoints]]
         (when (:transport/earliest-delivery-date transport)
           [:div.text-sm.font-bold.text-slate11
            (tr :transport/earliest-delivery-date) ": "
            (time-format/->str
             {:format :datetime :style :short :locale (locale/get-current-locale)}
             (:transport/earliest-delivery-date transport))])
         (when distance
           [:div.text-sm.font-bold.text-slate11
            (str (tr "Total distance") ": ") (util/format-distance distance)])
         (when (:ductile/note transport)
           [:div.text-sm.font-bold.text-slate11
            (str (tr :ductile/note) ": ") (:ductile/note transport)])]
        [:div.flex
         [:img.inline-block {:src (ui.logo/logo (-> transport :origin/location :compound/name))
                             :width 150
                             :height 60}]]]
       [:div
        (into [:ul.mt-4.border-t.border-black.pt-4]
              (map (fn [waypoint]
                     [:li.flex.gap-2.border-b.border-black.py-4.first:pt-0
                      [:div.w-4.h-4.border.border-2.border-black.rounded.flex-shrink-0
                       {:class "mt-[1px]"}]
                      [:div
                       [:div.text-sm.font-bold (str (:address/postcode waypoint) " " (:address/city waypoint))]
                       [:div.text-sm (:address/name waypoint) ", " (:address/street waypoint)]
                       (when (:location/delivery-modalities waypoint)
                         [:div.font-bold.mt-1 {:class "text-[11px]"} (:location/delivery-modalities waypoint)])
                       [:table.text-xs.mt-2
                        (into [:tbody]
                              (map (fn [fahrzeug]
                                     [:tr
                                      [:td.pr-2.py-1
                                       [:div.w-4.h-4.border.border-2.border-black.rounded]]
                                      [:td.pr-2.py-1
                                       [:span.font-bold (str-join-existing " " (:make/name fahrzeug) (:vehicle/model fahrzeug))] " "
                                       [:span (:vehicle/vin fahrzeug)]]
                                      #_[:td.py-1
                                         (when (:vehicle/equipment fahrzeug)
                                           [:div.flex.items-center.gap-2
                                            [ui/icon :backpack]
                                            (str/join ", " (:vehicle/equipment fahrzeug))])]]))
                              (:waypoint/fahrzeuge waypoint))]]]))
              waypoints)
        [:div.flex.gap-6.mt-8.items-center.justify-center
         [:div
          [ui/qr-code {:size 100} (str "https://" (.-host js/location) "/route-plan/" (:ductile/id transport))]]
         [:div
          [:img.flex-shrink-0 {:width 76 :height 100 :src "/images/scan-qr-code-hint.svg"}]]
         [:div.text-sm.font-bold
          (tr "Scan the QR code to open a digital route plan on you smartphone.")]]]]]]))

(defn mobile-route-plan [args _match]
  [mobile-route-plan-content args])

(defn route-plan [{:as args :keys [transport]} _match]
  [ui/tabs {:default-value :print}
   [:div.flex.items-center.justify-between.mb-3.text-xs.w-full.max-w-4xl.mx-auto.print:hidden
    [ui/link
     {:href (transport-link (merge transport {:compound-name (:compound/name args)}))}
     [ui/icon :arrow-left]
     (tr "Back to transport")]
    [:div.flex.gap-2
     [ui/tabs-list
      [ui/tab {:value :mobile} (tr "Mobile Preview")]
      [ui/tab {:value :print} (tr "Print Preview")]]
     [ui/button {:variant :primary :on-click #(js/window.print)}
      [ui/icon :file-text] (tr "Print")]]]
   [ui/tab-content {:value :print :class "py-2 print:p-0 print-margin min-h-a4"}
    [printable-route-plan args]]
   [ui/tab-content {:value :mobile :class "py-2 print:hidden"}
    [:div.flex.justify-center
     [mobile/iphone-portrait
      [mobile-route-plan-content (assoc args :layout/variant :simulate-device)]]]]])

(defn transport-progress [{:keys [by-model total done by-engine-type entry-transport?] :as t}]
  (let [progress-percent (when-not (zero? total)
                           (gstring/format "%.2f%"
                                           (* 100 (/ (or done 0) total))))]
    [:<>
     (when-not (zero? total)
       [:div.flex.flex-col.md:flex-row.gap-1.md:gap-2.text-sm
        [:span.font-bold (if entry-transport?
                           (tr "Unloading is %1 complete:" progress-percent)
                           (tr "Load is %1 complete:" progress-percent))]
        [:span.text-mauve11 (tr "%1 remaining, %2 done, %3 total"
                                (- total done)
                                (or done 0)
                                (or total 0))]])
     (when-not (zero? total)
       [:div.w-full.bg-mauve5.rounded-full.relative.mt-3.shadow-inner.overflow-hidden
        [:div.h-6.bg-indigo-700 {:style {:width progress-percent}}]])
     (when by-model
       [:div.overflow-x-auto.mt-3
        [:table.w-full.text-left.text-sm
         [:thead
          [:tr.text-mauve11 {:class "text-[10px] md:text-[12px]"}
           [:th.align-bottom.pb-1.text-black {:row-span 2 :class "text-[10px] md:text-sm"} (tr "Model")]
           [:th.align-bottom.border-r.border-slate7.leading-tight.pb-1 (tr "Total")]
           [:th.align-bottom.leading-tight.pl-2.pb-1 (tr :engine.type/internal-combustion)]
           [:th.align-bottom.leading-tight.pb-1 (tr :engine.type/hybrid)]
           [:th.align-bottom.leading-tight.pb-1 (tr :engine.type/plug-in-hybrid)]
           [:th.align-bottom.leading-tight.pb-1 (tr :engine.type/electric)]]
          [:tr.border-b-2.border-slate7 {:class "text-[10px] md:text-sm"}
           [:th.font-bold.border-r.border-slate7.pb-1 (or done 0) "/" (or total 0)]
           [:th.pl-2.pb-1 (get-in by-engine-type [:engine.type/internal-combustion :done] 0) "/" (get-in by-engine-type [:engine.type/internal-combustion :total] 0)]
           [:th.pb-1 (get-in by-engine-type [:engine.type/hybrid :done] 0) "/" (get-in by-engine-type [:engine.type/hybrid :total] 0)]
           [:th.pb-1 (get-in by-engine-type [:engine.type/plug-in-hybrid :done] 0) "/" (get-in by-engine-type [:engine.type/plug-in-hybrid :total] 0)]
           [:th.pb-1 (get-in by-engine-type [:engine.type/electric :done] 0) "/" (get-in by-engine-type [:engine.type/electric :total] 0)]]]
         (into [:tbody {:class "text-[11px] md:text-sm"}]
               (mapv (fn [model-data]
                       [:tr.border-t.border-slate7.odd:bg-mauve2
                        [:td.align-top {:class "py-0.5"} (:vehicle/model model-data)]
                        [:td.align-top.font-bold.border-r.border-slate7 {:class "py-0.5"} (get model-data :done 0) "/" (get model-data :total 0)]
                        [:td.align-top.pl-2 {:class "py-0.5"} (get-in model-data [:by-engine-type :engine.type/internal-combustion :done] 0) "/" (get-in model-data [:by-engine-type :engine.type/internal-combustion :total] 0)]
                        [:td.align-top {:class "py-0.5"} (get-in model-data [:by-engine-type :engine.type/hybrid :done] 0) "/" (get-in model-data [:by-engine-type :engine.type/hybrid :total] 0)]
                        [:td.align-top {:class "py-0.5"} (get-in model-data [:by-engine-type :engine.type/plug-in-hybrid :done] 0) "/" (get-in model-data [:by-engine-type :engine.type/plug-in-hybrid :total] 0)]
                        [:td.align-top {:class "py-0.5"} (get-in model-data [:by-engine-type :engine.type/electric :done] 0) "/" (get-in model-data [:by-engine-type :engine.type/electric :total] 0)]])
                     (sort-by :vehicle/model by-model)))]])]))

(defn bills-of-lading-table [{:as transport-fetch :keys [transport]} waypoints]
  [:table.w-full
   [:thead.uppercase.tracking-wide.text-left.border-b.border-slate4.text-mauve9
    {:class "text-[11px]"}
    [:tr
     [:th.py-1.px-2 (tr "Customer")]
     [:th.py-1.px-2 (tr "Destination")]
     [:th.py-1.px-2 (tr "Bill of lading")]
     [:th.py-1.px-2 ""]]]
   [:tbody {:class "text-[12px]"}
    (when-not (entry-transport? transport-fetch)
      (into [:<>]
            (map (fn [waypoint]
                   (let [{:keys [document generate! loading?]} (:waypoint/bill-of-lading waypoint)]
                     [:tr.align-top.border-b.last:border-b-0.border-slate4.odd:bg-mauve2
                      [:td.py-1.px-2 (:customer/name waypoint)]
                      [:td.py-1.px-2
                       (location-text waypoint) [:br]
                       (let [vehicle-count (count (:waypoint/fahrzeuge waypoint))]
                         [:span.inline-flex.gap-1.items-center
                          [ui/icon {:size 12} :car]
                          (str vehicle-count " " (trn ["vehicle" "vehicles"] vehicle-count))])]
                      [:td.py-1.px-2
                       (cond
                         loading?
                         [:span.flex.items-center.gap-1
                          [ui/spinner {:size 12}]
                          (str (tr "Generating") "…")]
                         document
                         [ui/link {:href (routes/path '[compound :compound-name bill-of-lading :ductile-id]
                                                      {:ductile-id (:ductile/id document)
                                                       :compound-name (:compound/name waypoint)})}
                          [ui/icon {:width 12 :height 12} :file]
                          (:document/number document)])]
                      [:td.px-2.py-1.text-right
                       (when-current-user-scope
                        :scope/bill-of-lading
                        [ui/link {:on-click generate! :variant :destructive :disabled? loading?}
                         [ui/icon {:width 12 :height 12} :update]
                         (if document (tr "Regenerate") (tr "Generate"))])]])))
            waypoints))
    (into [:<>]
          (map (fn [bill-of-lading]
                 [:tr.align-top.border-b.last:border-b-0.border-slate4.odd:bg-mauve2
                  [:td.py-1.px-2]
                  [:td.py-1.px-2]
                  [:td.py-1.px-2
                   [fahrzeug/file-view bill-of-lading]]
                  [:td.px-2.py-1.text-right]]))
          (filter :cas/multihash (:transport/bills-of-lading transport)))]])

(defn editable-transport? [transport-fetch]
  (not (italian-compound? (:compound/name transport-fetch))))


(def theme
  {:priority {:low "#43e9cf"
              :medium "#b0b0b0"
              :high "#ea3f3f"}
   :vis {:hover {"delivery" "#a557f7"}
         :selected {"delivery" "#581c87"}
         "pickup" "#e9590e"
         "delivery" "#9236ea"}})

(defn waypoints-map []
  (let [height 350]
    [:div
     (if @!route
       [overview-map/view {:standalone? false
                           :layout-height (str height "px")
                           :theme theme
                           :show-flows? false
                           :route @!route}]
       [:div.rounded.shadow.bg-slate-100 {:style {:height height}}])]))

(defn route-details [route]
  (let [{:keys [distance duration]} (route-metrics route)]
    [:<>
     (str (tr "Total distance") ": ")
     [:span.font-bold (util/format-distance distance)]
     (str ", " (tr "Total duration") ": ")
     [:span.font-bold (util/format-duration duration)]]))

(defn fahrzeug-link [fahrzeug]
  (router/path '[compound :compound-name vehicle :fahrzeug-id]
               {:compound-name (:compound/name fahrzeug)
                :fahrzeug-id (:vehicle/vin fahrzeug)}))

(defn show-transport [{:as transport-fetch :keys [transport loading-lines]} _match]
  (let [;; when an action updates the transport, it updates the route ::transport key for use,
        ;; otherwise use what comes in as an arg from the original fetch:
        transport @(deframe/!route-get ::transport transport)
        entry-transport? (entry-transport? transport-fetch)
        bulk-transport? (> (or (:transport/assumed-vehicles-count transport)
                               (count (:transport/fahrzeuge transport)))
                           10)
        transport-fetch (merge transport-fetch {:transport transport
                                                :entry-transport? entry-transport?})
        link-opts (into {:compound-name (:compound/name transport-fetch)} transport)
        line @(deframe/!route-get ::line (or (:transport/loading-line transport) (first loading-lines)))]
    (hooks/use-effect (fn []
                        (fields/set-current-load! transport)
                        (deframe/route-assoc! ::waypoints (make-waypoints transport-fetch))
                        (deframe/route-assoc! ::origin (make-origin transport))) [transport])
    [ui/panel
     [ui/panel-sidebar
      [:div {:class ["max-w-[360px]"]}
       [:div.flex.items-center.justify-between.mb-6
        [:h4.text-base (tr "At a glance")]
        (when (and
               (editable-transport? transport-fetch)
               (not @(rf/subscribe [::edit-params])))
          [ui/button
           {:size 1
            :data-testid "edit-load"
            :on-click #(deframe/route-assoc!
                        :edit-transport-form
                        (select-keys transport [:transport/carrier
                                                :transport/driver-name
                                                :transport/transporter
                                                :transport/name
                                                :transport/license-plate
                                                :truck/number]))}
           (tr "Edit load")])]
       [transport-data-list transport-fetch]]]
     [ui/panel-content
      [:div.flex.items-center.justify-between
       [:div.text-xs
        [ui/link
         {:href (if entry-transport?
                  (routes/path '[compound :compound-name entry-transports] link-opts)
                  (routes/path '[compound :compound-name transports] link-opts))}
         [ui/icon :arrow-left]
         (tr "Back to transports table")]]
       (if (and (not= :mode/ship (:transport/mode transport))
                (not entry-transport?))
         [:div.flex.items-center.gap-2
          [ui/button
           {:as :a :href (transport-link 'route-plan link-opts) :size 1}
           [ui/icon :file-text]
           (tr "Print route plan")]
          [ui/button
           {:as :a :href (transport-link 'load-list link-opts) :size 1}
           [ui/icon :file-text]
           (tr "Print load list")]]
         (when (contains? (:transport/contract-names transport) "Sixt Infleet")
           [:div.flex.items-center.gap-2
            [ui/button
             {:size 1
              :on-click (fn []
                          (ductile.ui.http/download
                           "/api/lambda"
                           {:lambda/handlers `[ductile.transport/handle-generate-label-pdf]
                            :label-name "sixt-labels-small"
                            :compound/name (or (-> transport :origin/location :compound/name)
                                               (:compound/name (deframe/current-route-params)))
                            :ductile/id (:ductile/id transport)}))}
             [ui/icon :file-text]
             (tr "Print small SIXT labels")]
            [ui/button
             {:size 1
              :on-click (fn []
                          (ductile.ui.http/download
                           "/api/lambda"
                           {:lambda/handlers `[ductile.transport/handle-generate-label-pdf]
                            :label-name "sixt-labels-big"
                            :compound/name (or (-> transport :origin/location :compound/name)
                                               (:compound/name (deframe/current-route-params)))
                            :ductile/id (:ductile/id transport)}))}
             [ui/icon :file-text]
             (tr "Print big SIXT labels")]]))]
      [:div.mt-6.mb-6.pb-4.border-b.pl-1
       [:h2.mt-6.mb-2.flex.items-center.gap-2
        (if entry-transport?
          (tr "Entry Transport")
          (tr "Transport"))
        [:span.text-slate7 [mode-icon {:size 28} transport]]]
       [:div.text-lg.text-slate11
        (cond
          (and (:transport/customer-names transport) entry-transport?)
          (str/join ", " (remove nil? (:transport/customer-names transport)))

          (and (not entry-transport?) (self-pickup? transport))
          (tr "self pickup")

          (or (#{:mode/ship :mode/train} (:transport/mode transport)) entry-transport?)
          (str (or (get-in transport [:origin/location :location/name])
                   (get-in transport [:origin/location :compound/name]))
               " → "
               (or (get-in transport [:destination/location :location/name])
                   (get-in transport [:destination/location :compound/name])))

          :else
          [route-preview transport @!origin @!waypoints])]
       [:div
        (when (ui/debug-mode?)
          [:div.mt-5
           [:div.bg-white.rounded-lg.shadow-lg.overflow-x-auto
            [:div.px-5.py-2.bg-slate-100.border-b
             (render/inspect transport)]]])]]
      (when-let [error @(deframe/!route-get ::error)]
        [ui/expected-error-view error])
      (when-current-user-scope
       #{:scope/train-entry :scope/inspect-workflow}
       [:div.mb-12
        [transport-status-form (assoc transport-fetch :line line) @!waypoints]])
      (when (and (not= :mode/ship (:transport/mode transport))
                 (not (self-pickup? transport)))
        [:div.mb-12
         [:div.flex.items-center.justify-between.mb-3
          [:h4.m-0 (tr "Bills of Lading")]]
         [bills-of-lading-table transport-fetch @!waypoints]])
      (when-not (:transport/manifest-unknown? transport)
        [:div.mb-12
         [:div.flex.items-center.justify-between.mb-3
          [:h4.m-0 (if entry-transport?
                     (tr "Unloading Progress")
                     (tr "Loading Progress"))]]
         [transport-progress (:transport/progress transport)]])
      (when-not bulk-transport?
        (if entry-transport?
          (when (not-empty (:transport/fahrzeuge transport))
            [:div.flex.flex-col.mb-12
             (into [:div.-mx-2.mt-3.rounded-md.border.border-b-0.border-slate-300.overflow-hidden]
                   (map (fn [fahrzeug]
                          [fahrzeug/vehicle-details-header
                           {:href (fahrzeug-link fahrzeug)}
                           fahrzeug]))
                   (:transport/fahrzeuge transport))])
          [:div.mb-12
           [:div.flex.items-center.justify-between.mb-3
            [:div
             [:h4.m-0 (tr "Waypoints")]
             [:div.text-xs.mt-1
              (if @(deframe/!route-get ::fetching-route?)
                (str (tr "Loading route details") "…")
                [route-details @!route])]]
            (when (and (seq @!waypoints) (< 1 (count @!waypoints)))
              [ui/button
               {:variant :secondary
                :on-click #(fetch-route! {:origin @!origin
                                          :waypoints @!waypoints
                                          :optimize? true
                                          :transport transport})}
               (str "🪄 " (tr "Sort by optimal route"))])]
           [:div.flex.flex-col.gap-4
            (when-not entry-transport?
              [waypoints-map])
            [waypoints-list {:waypoints/editable? true :layout/show-origin? (not entry-transport?)} transport-fetch]]]))]
     (when @(deframe/!route-get :edit-transport-form)
       [fields/edit-transport-dialog
        {:on-submit (fn [load] ($edit-transport load))
         :fixed-params {:transport/mode :mode/truck}
         :available-actions (:transport/available-actions transport)
         :show-debug-output? (ui/debug-mode?)
         :on-close #(deframe/route-dissoc! :edit-transport-form)}])]))

(defn load-summary [load _match]
  (let [origin (:origin/location load)]
    [:div
     [:div.flex.items-center.justify-end.text-xs.w-full.max-w-4xl.mx-auto.print:hidden
      [:div
       [:button.inline-flex.flex-shrink-0.items-center.justify-center.font-medium.leading-none.outline-none.rounded.px-2.text-xs.gap-1.text-white.bg-indigo-600.hover:bg-indigo-700.active:bg-indigo-500.focus:shadow-indigo-700
        {:on-click #(js/window.print)
         :class ["focus:shadow-[inset_0_0_0_1px]" "h-[32px]"]}
        icons/document (tr "Print")]]]
     [:div.border.bg-gray-200.w-full.print:w-auto.p-5.not-prose.print:p-0.print:bg-white.print-margin.print:border-transparent
      [:div.screen:min-h-screen
       [:div.mx-auto
        [:div.bg-white.max-w-4xl.mx-auto.p-12.shadow-lg.relative.print:max-w-none.print:shadow-none.print:p-0.page-break-avoid
         [:div.border-b-2.border-black.pb-4.flex.items-end.justify-between.mb-6
          [:div
           [:h1.text-2xl.font-bold.mt-0 (:load/id load)]
           [:div.text-lg
            [:span (str/join " - "
                             (remove nil? [(-> load :transport/transporter :truck/number)
                                           (-> load :transport/carrier :address/name)
                                           (-> load :transport/license-plate)
                                           (-> load :transport/driver-name)]))]]
           [:div.text-lg
            [:span.font-bold (tr "Created at") ": "]
            [:span
             (time-format/->str (:ductile/created-at load))]]]
          [:img.max-w-xs {:src (ui.logo/ars-logo)}]]
         [:div.mt-4.mb-8
          [:div.font-bold.text-lg (tr "Waypoints")]
          [waypoints-viewer (assoc load :origin origin :waypoints (make-waypoints {:transport load}))]]
         [:div.mt-4.mb-8
          [:div.font-bold.text-lg (tr "Origin")]
          [:span
           (str/join ", " [(-> origin :compound/company :company/name)
                           (:address/street origin)
                           (str (:address/postcode origin) " " (:address/city origin) " " "(" (:address/province-code origin) ")")])]]
         [:div.my-4
          [:div.font-bold.text-lg (tr "Destinations")]
          (into [:<>]
                (map (fn [[destination fahrzeuge]]
                       [:div.flex.border-t.border-black.pt-2.mt-2.mb-4
                        [:div {:class "w-1/2"} [:span.font-bold (:address/name destination)]
                         [:div.mt-2 {:class "text-[16px]"}
                          (:address/street destination) [:br]
                          (:address/postcode destination) " "
                          (:address/city destination) " " "(" (:address/province-code destination) ")"]]
                        [:table.w-full.relative {:class "w-1/2 -mt-2 -top-[1px]"}
                         (into [:tbody]
                               (map-indexed (fn [i fahrzeug]
                                              [:tr.border.border-black.first:border-t.last:border-b
                                               [:td.py-1 {:class "text-[14px] pl-[2mm] h-[40px]"} (inc i)]
                                               [:td.py-1 {:class "text-[14px] pl-[2mm] h-[40px]"} (:vehicle/vin fahrzeug)]
                                               [:td.py-1 {:class "text-[14px] pl-[2mm]"} (str (:make/name fahrzeug) " " (:vehicle/model fahrzeug))]]))
                               fahrzeuge)]])
                     (group-by :fahrzeug/destination (:transport/fahrzeuge load))))]]]]]]))
